/**
 * Bilingual FR/EN dictionary for Océanne Brodeur website.
 * Source: brief/02-strategie-intralys.md + design doc Section 1.
 *
 * - Signature copy (Hero, 3 Peurs, Méthode, Promesse, Tagline) is transcribed
 *   verbatim FR from the brief, with professional EN placeholders marked
 *   "[PLACEHOLDER]" pending Océanne's review or a commissioned translation.
 * - Long-body sections (FAQ answers, mentions légales, bio, offre details) use
 *   stubs ("À écrire" / "To be written") and will be filled when Phase 3
 *   sections are implemented.
 * - Strings identical in FR/EN (proper nouns, codes) use plain string form.
 */

export const t = {
  // === BRAND ===
  brand: 'Océanne Brodeur',
  tagline: {
    fr: 'Protéger ceux qu\'on aime — avant le pire.',
    en: 'Protect the ones you love, before the worst arrives.'
  },

  // === NAVIGATION ===
  nav: {
    accueil: { fr: 'Accueil', en: 'Home' },
    apropos: { fr: 'À propos', en: 'About' },
    methode: { fr: 'Méthode', en: 'Method' },
    offre: { fr: 'Offre', en: 'Offer' },
    guide: { fr: 'Guide', en: 'Guide' },
    ressources: { fr: 'Ressources', en: 'Resources' },
    faq: { fr: 'FAQ', en: 'FAQ' },
    contact: { fr: 'Contact', en: 'Contact' },
    consultation: { fr: 'Audit gratuit', en: 'Free audit' },
    lang_toggle_fr: { fr: 'FR', en: 'FR' },
    lang_toggle_en: { fr: 'EN', en: 'EN' }
  },

  // === TRUST SIGNALS — credentials chips + microcopy chips réutilisables ===
  // Sources vérifiées 2026-05-22→24 + agence update 2026-05-29 :
  // AMF cert 271218 + cabinet iA Groupe financier — Agence Beaubien (Montréal)
  // + Podium iA 2025 2e place Conseillère-Recrue + 100 % AMG positioning.
  trust: {
    // A — AmfTrustBadge : 2 versions selon contexte (chip court vs standalone complet).
    // Phase 27.33 — Codex audit #1 fix : titre réglementaire AMF complet en visible
    // pour les placements standalone (MidPageCta + Offer + Contact). Le chip court
    // reste utilisé dans TrustStrip Hero (4 chips compactes côte-à-côte).
    amf_badge: {
      fr: 'Inscrite AMF · n° 271218',
      en: 'AMF registered · cert. 271218'
    },
    amf_badge_full: {
      fr: 'Conseillère en sécurité financière · Certificat AMF n° 271218',
      en: 'Financial security advisor · AMF certificate no. 271218'
    },
    amf_badge_aria: {
      fr: 'Inscrite à l\'Autorité des marchés financiers du Québec, certificat n° 271218',
      en: 'Registered with the Autorité des marchés financiers of Quebec, certificate no. 271218'
    },
    strip_aria: {
      fr: 'Credentials Océanne Brodeur',
      en: 'Océanne Brodeur credentials'
    },

    // C — TrustStrip : 4 chips horizontal cluster (Hero)
    strip_amg: {
      fr: 'Spécialiste 100 % AMG',
      en: '100 % AMG specialist'
    },
    strip_cabinet: {
      fr: 'iA Groupe financier · Montréal',
      en: 'iA Financial Group · Montréal'
    },
    strip_award: {
      fr: '2e place Podium iA 2025 · Recrue',
      en: '2nd place Podium iA 2025 · New advisor'
    },

    // B — microcopy chips contextualisés
    microcopy_methode: {
      fr: '5 à 6 questions · généralement sans prise de sang',
      en: '5 to 6 questions · usually no blood test'
    },
    microcopy_offer: {
      fr: '3 soumissions : Manuvie · Canada-Vie · Sun Life',
      en: '3 quotes: Manulife · Canada Life · Sun Life'
    },
    microcopy_contact: {
      fr: '45 min · sans engagement · audit gratuit',
      en: '45 min · no commitment · free audit'
    }
  },

  // === HERO ===
  hero: {
    eyebrow: { fr: 'Conseillère en sécurité financière · Spécialiste AMG · Québec', en: 'Financial Security Advisor · Critical Illness Specialist · Quebec' },
    // Phase 27.33-B — Codex audits #1+#2 fix : ajout "comme diagnostic" pour
    // défensibilité AMF (claim absolu → claim factuellement scoped). Le brief
    // CH.3 + stat4 utilisent déjà "comme diagnostic" verbatim, donc cet ajout
    // aligne le Hero avec le reste du copy déjà validé. Pas de divergence brief.
    title: {
      fr: 'Votre régime collectif ne couvre pas le cancer comme diagnostic.',
      en: 'Your group plan does not cover cancer as a diagnosis.'
    },
    title_italic: {
      fr: 'Il couvre l\'invalidité — ce n\'est pas la même chose.',
      en: 'It covers disability, those are not the same.'
    },
    subtitle: {
      fr: 'Pour les familles qui ont déjà vu un cancer de près. Et qui refusent de revivre ça sans bouclier.',
      en: 'For families who have already seen cancer up close. And who refuse to live through it again without a shield.'
    },
    cta_primary: { fr: 'Réserver l\'audit gratuit', en: 'Book the free audit' },
    cta_secondary: { fr: 'Lire pourquoi je fais ce travail', en: 'Read why I do this work' },
    // 2026-06-11 — « 28 000 $ » et « chimio expérimentale » réalignés sur le vérifié :
    // tiers des familles les plus touchées = jusqu'à 961 $/mois de leur poche
    // (Longo 2021) → « près de 1 000 $ par mois » sur 18 mois ≈ 17-18 K$, défendable.
    marie_pier_quote: {
      fr: '« J\'ai vu ma sœur y laisser près de 1 000 $ par mois, de sa poche, pendant 18 mois. Les médicaments que la RAMQ ne couvrait pas. Les transports à Montréal. Le congé non payé de son chum. Mon père qui a pigé dans son REER pour aider. Pendant ce temps-là, Sophie luttait pour sa vie. Et je me suis dit : si ça m\'arrive, mes enfants ne verront pas leur mère pleurer parce qu\'elle paie l\'épicerie. »',
      en: '"I watched my sister lose nearly $1,000 a month, out of pocket, for 18 months. Medication RAMQ didn\'t cover. The Montreal commute. Her partner\'s unpaid leave. My father pulling from his RRSP to help. All while Sophie was fighting for her life. That\'s when I told myself: if this happens to me, my children will never see their mother cry over a grocery bill."'
    },
    marie_pier_attribution: {
      fr: '— Ce que mes clients me racontent, souvent en pleurant',
      en: '— What my clients tell me, often in tears'
    },
    dedication: {
      fr: '« On commence par votre situation, pas un produit. »',
      en: '"We start with your situation, not a product."'
    },
    // Phase 27.32 — Gemini audit alignment : tighten clôture vers code empathique
    // CH.6 #7 verbatim "Si ce n'est pas pertinent pour vous, je vous le dirai."
    body: {
      // Phase 27.63 — découpé en 2 paragraphes (séparateur \n\n) pour
      // alléger visuellement sans toucher au texte. Le composant Hero
      // split sur \n\n et render un <p> par bloc. Bloc 1 = définition +
      // promesse audit. Bloc 2 = filtre soumissions + clause de sortie.
      // SEO identique (mêmes mots dans le DOM).
      fr: 'Le « régime collectif », c\'est l\'assurance offerte par votre employeur. Je vous montre exactement ce qu\'il couvre, et ce qu\'il ne couvre pas. Première rencontre : 45 minutes, sans engagement. Vous repartez avec un document écrit.\n\nSi une assurance maladie grave est pertinente pour votre situation, je demande trois soumissions à Manuvie, Canada-Vie et Sun Life. Si ce n\'est pas pertinent pour vous, je vous le dirai.',
      en: 'Your "group plan" is the insurance offered by your employer. I show you exactly what it covers, and what it does not. First meeting: 45 minutes, no commitment. You leave with a written document.\n\nIf critical illness insurance makes sense for your situation, I request three quotes from Manulife, Canada Life, and Sun Life. If it is not right for you, I will tell you so.'
    },
    cta_primary_full: {
      fr: 'Réserver l\'audit gratuit (45 min)',
      en: 'Book the free audit (45 min)'
    },
    cta_secondary_full: {
      fr: 'Comprendre la méthode 5 questions',
      en: 'Understand the 5 Questions method'
    },
    trust_microcopy: {
      fr: 'Inscrite AMF · 5 à 6 questions · généralement sans prise de sang',
      en: 'AMF registered · 5 to 6 questions · usually no blood test'
    }
  },

  // === STATS ===
  stats: {
    // Phase 27.32 — tighten éditorial dérivé (pas verbatim brief). Récupère le
    // code empathique CH.6 "Voici exactement ce que..." → "Voici les chiffres réels."
    eyebrow: { fr: 'Voici les chiffres réels.', en: 'Here are the real numbers.' },
    stat1_value: '2/5',
    stat1_label: { fr: 'Canadiens développeront un cancer dans leur vie', en: 'Canadians will develop cancer in their lifetime' },
    // 2026-06-11 — stats VÉRIFIÉES aux sources primaires (cf docs/guide-amg/RECHERCHE.md).
    // « 22 % avant 50 ans » = FAUX (réalité ~1/10, SCC) ; « 78 % » = introuvable (inventé) ;
    // 33 000 $ = vie entière personne+proches (SCC 2024), pas « hors poche sur 24 mois ».
    stat2_value: '1/10',
    stat2_label: { fr: 'des nouveaux cas surviennent avant 50 ans', en: 'of new cases occur before age 50' },
    stat3_value: '33 000 $',
    stat3_label: {
      fr: 'de coûts moyens liés au cancer, pour la personne et ses proches, au cours de la vie',
      en: 'in average lifetime cancer-related costs, for the person and their loved ones'
    },
    stat4_value: '9 %',
    stat4_label: {
      fr: 'des Canadiens seulement détiennent une assurance maladies graves',
      en: 'of Canadians actually own critical illness insurance'
    },
    stat5_value: '1/8',
    stat5_label: { fr: 'femmes au Canada développeront un cancer du sein', en: 'women in Canada will develop breast cancer' },
    // r.10 art. 13 — toute statistique écrite doit indiquer sa source.
    sources: {
      fr: 'Sources : Société canadienne du cancer (2026) · Rapport spécial SCC, Statistique Canada et ASPC (2024)',
      en: 'Sources: Canadian Cancer Society (2026) · CCS special report with Statistics Canada and PHAC (2024)'
    }
  },

  // === ABOUT ===
  about: {
    eyebrow: { fr: 'Pourquoi je fais ce travail', en: 'Why I do this work' },
    // Phase 27.32 — tighten éditorial dérivé. Brief CH.A pull_quote validé verbatim :
    // "Je n'ai pas voulu vendre dix produits. J'ai choisi d'en comprendre un seul,
    // à fond." → on récupère le concept en titre H2 plus direct.
    title: {
      fr: '100 % AMG. Un seul produit, à fond.',
      en: '100 % critical illness. One product, fully.'
    },
    // Phase 27.32 — Gemini audit alignment : ajout code empathique CH.6 #1
    // "Je comprends ce que vous avez vu." en ouverture (validation persona).
    pull_quote: {
      fr: 'Je comprends ce que vous avez vu. C\'est pour ça que je n\'ai pas voulu vendre dix produits — j\'ai choisi d\'en comprendre un seul, à fond.',
      en: 'I understand what you have seen. That\'s why I didn\'t want to sell ten products. I chose to understand one, fully.'
    },
    bio: {
      // Phase 27.63 — bio split en 3 paragraphes. Phase 27.67 — tighten conservatif.
      // Phase 27.69 — dédup 2e niveau : para 2 ne répète plus pull_quote ; para 3
      // ne répète plus hero.body (lu 1 section plus haut). Voix Océanne préservée
      // (signatures "Mon travail est simple", "vrai contrat pas un PDF", "Vous décidez").
      // 2026-06-11 — chiffre recadré sur le vérifié : 33 000 $ = coûts à vie
      // personne+proches (SCC 2024). « Chimio expérimentale non couverte RAMQ »
      // retiré (trompeur : essais cliniques = gratuits, RAMQ universelle au QC).
      fr: 'Quand un cancer tombe dans une famille, les coûts s\'accumulent vite : en moyenne 33 000 $ assumés par la personne et ses proches — dépenses de leur poche, temps consacré aux soins, revenus perdus (Société canadienne du cancer, 2024). Transports au centre oncologique. Congé non payé du conjoint. Parents qui pigent dans leur REER. C\'est ce que j\'ai vu dans mon entourage, et ce que mes clients me racontent, souvent en pleurant.\n\nUn seul produit, à fond : l\'assurance maladie grave. Capital forfaitaire de 100 000 à 500 000 $, versé le jour d\'un diagnostic couvert.\n\nMon travail est simple : on lit votre vrai contrat collectif ensemble, pas un PDF. Vous décidez. Si une assurance maladie grave n\'est pas pertinente, je vous le dis aussi.',
      en: 'When cancer hits a family, the costs add up fast: on average $33,000 borne by the person and their loved ones — out-of-pocket expenses, time devoted to care, lost income (Canadian Cancer Society, 2024). Transport to the oncology center. Partner\'s unpaid leave. Parents pulling from their RRSP. That is what I saw in my own circle, and what my clients tell me, often in tears.\n\nOne product, fully: critical illness insurance. A lump sum of $100,000 to $500,000, paid the day a covered diagnosis arrives.\n\nMy work is simple: we read your actual group-plan contract together, not a PDF. You decide. If critical illness insurance is not right for you, I tell you that too.'
    },
    chip_empathie: { fr: 'Empathie', en: 'Empathy' },
    chip_protection: { fr: 'Protection', en: 'Protection' },
    chip_integrite: { fr: 'Intégrité', en: 'Integrity' }
  },

  // === 3 PEURS INAVOUÉES ===
  fears: {
    eyebrow: { fr: 'Les peurs qu\'on n\'ose pas dire', en: 'The fears no one says out loud' },
    title: {
      fr: 'Trois peurs qu\'on n\'ose pas nommer — qu\'on entend tous les jours.',
      en: 'Three fears no one dares to name, and that I hear every day.'
    },
    fear1_title: {
      fr: 'Devenir le fardeau financier de sa famille',
      en: 'Becoming your family\'s financial burden'
    },
    fear1_quote: {
      fr: 'Et si c\'est moi qui tombe malade, et que ma mère doit piger dans son REER pour nous ?',
      en: 'What if I\'m the one who gets sick, and my mother has to pull from her RRSP for us?'
    },
    // Phase 27.66 — split réponses sur \n\n : diagnostic (phrase 1) puis posture/résolution (phrase 2+).
    // Guillemets « » entourent naturellement le bloc même fragmenté.
    fear1_response: {
      fr: '« L\'assurance maladie grave, c\'est ce qui vous évite le moment GoFundMe, celui où la famille élargie pige dans ses REER pour vous aider.\n\nVous traversez votre crise sans devoir y entraîner les autres. Elle reste la vôtre — pas la leur. »',
      en: '"Critical illness insurance is what spares you the GoFundMe moment, the one where the extended family pulls from their RRSPs to help.\n\nYou go through your crisis without dragging the rest in. It stays yours, not theirs."'
    },
    fear2_title: {
      fr: 'Découvrir trop tard qu\'on n\'était pas couvert',
      en: 'Finding out too late that you weren\'t covered'
    },
    fear2_quote: {
      fr: 'Et si mon régime collectif ne couvre pas vraiment ce que je pense ?',
      en: 'What if my group plan doesn\'t really cover what I think it does?'
    },
    fear2_response: {
      fr: '« Mon premier travail avec vous, c\'est de vérifier exactement ce que votre régime collectif couvre, et surtout ce qu\'il ne couvre pas.\n\nVoici exactement ce que votre collectif couvre. Vous saurez la vérité aujourd\'hui, pas le jour du diagnostic. »',
      en: '"My first job with you is to verify exactly what your group plan covers, and above all, what it doesn\'t.\n\nHere is exactly what your group plan covers. You know the truth today, not on diagnosis day."'
    },
    fear3_title: {
      fr: 'Lutter pour sa vie ET pour son compte de banque',
      en: 'Fighting for your life AND your bank account'
    },
    fear3_quote: {
      fr: 'Si je dois choisir entre le traitement que la RAMQ ne couvre pas et l\'hypothèque, je choisirai laquelle ?',
      en: 'If I have to choose between the treatment RAMQ doesn\'t cover and the mortgage, which one do I pick?'
    },
    fear3_response: {
      fr: '« Un cancer n\'est pas le moment de calculer. C\'est le moment de guérir.\n\nL\'assurance maladie grave peut verser de 100 000 à 500 000 $ en capital selon le contrat retenu, pour que vous puissiez vous concentrer sur vivre, pas sur compter. »',
      en: '"Cancer is not the time to do math. It\'s the time to heal.\n\nCritical illness insurance can pay $100,000 to $500,000 in capital depending on the chosen contract, so you can focus on living, not on counting."'
    },
    reponse_label: {
      fr: 'Réponse d\'Océanne',
      en: 'Océanne\'s response'
    }
  },

  // === MÉTHODE 5 QUESTIONS (6 étapes) ===
  methode: {
    eyebrow: { fr: 'La Méthode 5 Questions', en: 'The 5 Questions Method' },
    // Phase 27.33-B — Codex audit #1+#2 fix : ajout "souvent" pour défensibilité AMF
    // (promesse opérationnelle dépend dossier/assureur). Promesse verbatim brief
    // ci-dessous garde la formulation originale (citation « » Océanne).
    title: {
      fr: 'De la première conversation à la signature : souvent moins de 10 jours.',
      en: 'From first conversation to signature: often under 10 days.'
    },
    // Phase 27.32 — Gemini audit alignment :
    // — ajout code empathique CH.6 #5 "Vous n'avez pas à parler aux assureurs."
    // — upgrade "Pas de pression" → "Aucune pression — c'est votre rythme." (code #2 verbatim)
    promesse: {
      // Phase 27.64 — split sur \n\n : promesse temporelle + simplicité administrative.
      // Mêmes mots, aération visuelle banner italique.
      fr: '« De la première conversation à la signature du contrat : souvent moins de 10 jours quand le dossier est simple.\n\nPas de prise de sang pour la plupart des profils admissibles. Pas de paperasse. Vous n\'avez pas à parler aux assureurs. Aucune pression — c\'est votre rythme. »',
      en: '"From the first conversation to a signed contract: often under 10 days when the file is simple.\n\nNo blood test for most eligible profiles. No paperwork. You never speak directly to insurers. No pressure — your pace, always."'
    },
    // Phase 27.67 — tighten ~20% sur les 6 step_desc (cuts filler).
    step1_label: { fr: 'Étape 01', en: 'Step 01' },
    step1_title: { fr: 'Rencontre initiale', en: 'Initial meeting' },
    step1_desc: {
      fr: '45 minutes. Premier appel sans engagement. Je vous écoute : situation, vécu, famille. Aucun produit présenté. Le but : comprendre vos besoins, pas vendre.',
      en: '45 minutes. First call, no commitment. I listen: situation, story, family. No product shown. The goal: understand your needs, not sell.'
    },
    step2_label: { fr: 'Étape 02', en: 'Step 02' },
    step2_title: { fr: 'Audit du collectif', en: 'Group plan audit' },
    step2_desc: {
      fr: 'Vous apportez votre contrat collectif. J\'identifie les trous : cancer absent en diagnostic autonome, plafonds limités, exclusions cachées. Vous repartez avec un document écrit.',
      en: 'You bring your group plan. I identify the gaps: no cancer as a standalone diagnosis, capped limits, hidden exclusions. You leave with a written document.'
    },
    step3_label: { fr: 'Étape 03', en: 'Step 03' },
    step3_title: { fr: 'Les 5 questions', en: 'The 5 questions' },
    step3_desc: {
      fr: 'Antécédents familiaux, médicaments, hospitalisations récentes, tabagisme, voyages à risque. 5 à 6 questions suffisent à pré-qualifier votre dossier. C\'est le filtre qui change tout.',
      en: 'Family history, medications, recent hospitalizations, smoking, high-risk travel. 5 to 6 questions to pre-qualify your file. That\'s the filter that changes everything.'
    },
    step4_label: { fr: 'Étape 04', en: 'Step 04' },
    step4_title: { fr: '3 soumissions comparatives', en: '3 comparative quotes' },
    step4_desc: {
      // Phase 27.75 — Codex 2e passe : cut "vous voyez" / "you see" (verbe redondant).
      fr: 'J\'envoie votre dossier à Manuvie, Canada-Vie et Sun Life. Vous recevez un tableau côte à côte : prime mensuelle, capital, exclusions. Pas de présélection — les trois.',
      en: 'I send your file to Manulife, Canada Life, Sun Life. You receive a side-by-side table: monthly premium, capital, exclusions. No pre-selection — all three.'
    },
    step5_label: { fr: 'Étape 05', en: 'Step 05' },
    step5_title: { fr: 'Signature numérique', en: 'Digital signature' },
    step5_desc: {
      // Phase 27.68 — dédup : cut "Aucune prise de sang..." (déjà dans methode.promesse banner).
      fr: 'Choix fait, signature électronique. Contrat émis en 7 à 10 jours. Aucun déplacement.',
      en: 'Once chosen, electronic signature. Contract issued in 7 to 10 days. No travel.'
    },
    step6_label: { fr: 'Étape 06', en: 'Step 06' },
    step6_title: { fr: 'Suivi annuel à vie', en: 'Annual follow-up for life' },
    step6_desc: {
      fr: 'Chaque année, on vérifie si la situation a changé : mariage, naissance, achat d\'une maison, nouveau revenu. Votre couverture s\'ajuste avec votre vie.',
      en: 'Every year we check what changed: marriage, birth, home purchase, new income. Your coverage adjusts with your life.'
    }
  },

  // === COÛT RÉEL CANCER ===
  // 2026-06-11 — section RECONSTRUITE sur les chiffres vérifiés (cf docs/guide-amg/
  // RECHERCHE.md). L'ancien « 60 000 — 100 000 $ sur deux ans » = 2-3× la référence
  // nationale À VIE (32 778 $, SCC 2024) → indéfendable (art. 16 + r.10 art. 13).
  // Nouveau cadre : scénario première année 20 000-54 000 $ selon couverture,
  // présenté comme exemple calculé, chaque brique sourcée.
  couts: {
    eyebrow: { fr: 'Le coût réel d\'un cancer', en: 'The real cost of cancer' },
    // 2026-06-11 panel — faille math : « 20 000 $ » ne sortait pas des hypothèses
    // affichées (30-40 % du salaire moyen + 6 200 $ = ≈26 000 $ minimum) → corrigé.
    big_number: '26 000 — 54 000 $',
    subtitle: {
      fr: 'Le creux que peut creuser une première année de cancer au Québec, selon votre couverture — exemple calculé pour un salaire moyen, détaillé ci-dessous.\n\nMême avec la carte Soleil. Même avec une bonne job. Voici les chiffres.',
      en: 'The hole a first year of cancer can dig in Quebec, depending on your coverage — a worked example at the average salary, detailed below.\n\nEven with the RAMQ card. Even with a good job. Here are the numbers.'
    },
    cat1_label: { fr: 'Dépenses de votre poche', en: 'Out-of-pocket spending' },
    cat1_desc: {
      fr: 'Médicaments non couverts, soins, déplacements, frais imprévus : 518 $ par mois en moyenne pendant le traitement — et jusqu\'à 961 $ par mois pour le tiers des familles les plus touchées (étude nationale canadienne, 901 patients, 2021).',
      en: 'Uncovered medications, care, travel, unplanned expenses: $518 a month on average during treatment — and up to $961 a month for the hardest-hit third of families (Canadian national study, 901 patients, 2021).'
    },
    cat2_label: { fr: 'Transports et déplacements', en: 'Transport and travel' },
    cat2_desc: {
      fr: 'Allers-retours à l\'hôpital, stationnement, repas hors maison, hébergement quand le traitement spécialisé est à Montréal et que vous habitez en région.\n\nTransport et stationnement seuls : environ 263 $ par mois pendant le traitement (même étude nationale).',
      en: 'Hospital round-trips, parking, meals out, lodging when specialized treatment is in Montreal and you live outside the city.\n\nTransport and parking alone: about $263 a month during treatment (same national study).'
    },
    cat3_label: { fr: 'Revenus perdus', en: 'Lost income' },
    cat3_desc: {
      fr: 'Sans invalidité collective, l\'assurance-emploi maladie verse 55 % du salaire, plafonné à 729 $ par semaine, pendant 26 semaines maximum : pour un salaire moyen au Québec, c\'est un trou d\'environ 14 800 $ en six mois — puis plus rien (Canada.ca, 2026).\n\nAvec invalidité collective, il manque encore 30 à 40 % du salaire chaque mois. Et vos parents finissent par piger dans leur REER pour boucler le mois.',
      en: 'Without group disability coverage, EI sickness benefits pay 55% of salary, capped at $729 a week, for a maximum of 26 weeks: at the average Quebec salary, that\'s a gap of about $14,800 over six months — then nothing (Canada.ca, 2026).\n\nWith group disability, you\'re still missing 30 to 40% of your salary every month. And your parents end up pulling from their RRSP to make the month work.'
    },
    sophie_ref: {
      // Exemple fictif ÉTIQUETÉ (Ligne directrice s.5.5 + code art. 16) + chiffre
      // aligné sur le vérifié (961 $/mois, tiers le plus touché — Longo 2021).
      fr: '« Exemple fictif, inspiré de situations réelles : pour Sophie, 18 mois de traitement et des centaines de dollars de sa poche chaque mois — comme le tiers des familles les plus touchées (jusqu\'à 961 $/mois, étude nationale 2021). C\'est ce trou que l\'assurance maladie grave vient combler : un seul versement forfaitaire, déposé dans votre compte. »',
      en: '"A fictional example, drawn from real situations: for Sophie, 18 months of treatment and hundreds of dollars out of pocket every month — like the hardest-hit third of families (up to $961/month, national study, 2021). That\'s the hole critical illness insurance fills: one single lump-sum payment, deposited in your account."'
    },
    cta: { fr: 'Réserver l\'audit gratuit', en: 'Book the free audit' }
  },

  // Phase 27.68 — section "axes" supprimée (dead code, jamais référencée dans
  // aucun composant). Les 3 messages (Urgence/Rébellion/Simplicité) étaient
  // répétés cross-sections : urgence_body = écho stats_strip + couts;
  // rebellion_body = écho about.bio "100% AMG, une seule spécialité";
  // facilite_body = écho methode.promesse + offre.component4_desc.

  // === DÉCLENCHEURS DE VIE (CH.4 du rapport — qui devrait s'en parler) ===
  declencheurs: {
    eyebrow: { fr: 'Est-ce le bon moment ?', en: 'Is it the right time?' },
    // Phase 27.32 — tighten éditorial dérivé. Brief CH.4 mentionne "4 déclencheurs
    // qui activent l'ICP" (factuel). Title actuel "rarement par hasard qu'on me
    // trouve" trop narcissique. Remplace par formulation neutre + intro qui
    // recycle le code empathique CH.6 "Si ce n'est pas pertinent pour vous, je
    // vous le dirai".
    title: {
      fr: 'Quatre moments qui changent la question.',
      en: 'Four moments that change the question.'
    },
    intro: {
      fr: 'Vous vous reconnaissez ? L\'audit gratuit vous dira si l\'assurance maladie grave est pertinente — sinon je vous le dirai.',
      en: 'Does this sound like you? The free audit will tell you whether critical illness insurance fits your situation — and if it doesn\'t, I\'ll tell you so.'
    },
    chip_recognize: { fr: 'Vous reconnaissez-vous ?', en: 'Does this sound like you?' },

    // Phase 27.64 — split cards body sur \n\n.
    // Phase 27.67 — tighten ~20% (cuts filler, sens préservé).
    card1_title: { fr: 'Vous venez d\'acheter une première maison', en: 'You just bought your first home' },
    card1_body: {
      fr: 'Une hypothèque, c\'est 25 ans d\'engagement. Mais votre revenu n\'est garanti que tant que vous êtes en santé.\n\nSi un diagnostic tombe, l\'hypothèque pèse le plus lourd. C\'est elle qu\'on protège en premier.',
      en: 'A mortgage is a 25-year commitment. But your income is only guaranteed while you\'re healthy.\n\nIf a diagnosis hits, the mortgage weighs the heaviest. That\'s what we protect first.'
    },

    card2_title: { fr: 'Vous attendez ou venez d\'avoir un enfant', en: 'You\'re expecting or just had a child' },
    card2_body: {
      // Phase 27.75 Codex 2e passe (EN only) : cut "It's" devant la liste (staccato).
      fr: 'Dès qu\'un enfant dépend de vous, ce n\'est plus juste vous qu\'on protège. C\'est l\'épicerie, la garderie, la maison.\n\nC\'est aussi le moment où la prime est la plus basse, la santé à son meilleur.',
      en: 'The moment a child depends on you, it\'s no longer just you we protect. Groceries, daycare, the home.\n\nIt\'s also when the premium is lowest, health at its best.'
    },

    card3_title: { fr: 'Un proche a reçu un diagnostic', en: 'Someone close received a diagnosis' },
    card3_body: {
      fr: 'Vous avez vu de près ce que ça coûte, émotionnellement, financièrement. Vous ne voulez pas que votre famille traverse la même chose sans bouclier.\n\nC\'est souvent comme ça que mes clients m\'arrivent.',
      en: 'You\'ve seen up close what it costs — emotionally, financially. You don\'t want your family to go through the same thing unprotected.\n\nThat\'s often how my clients come to me.'
    },

    card4_title: { fr: 'Vous avez 28 à 37 ans, salariée, avec un collectif', en: 'You\'re 28 to 37, salaried, with a group plan' },
    card4_body: {
      // Phase 27.75 Codex 2e passe : parenthèses tightened (cut "il couvre" / "it covers").
      fr: 'C\'est l\'âge optimal pour souscrire : santé au meilleur, prime au plus bas. Le piège du collectif (invalidité, pas cancer) est rarement expliqué.\n\nC\'est exactement ce que mon audit gratuit éclaire.',
      en: 'It\'s the optimal age to apply: health at its best, lowest premium. The group-plan trap (disability, not cancer) is rarely explained.\n\nThat\'s exactly what my free audit clarifies.'
    }
  },

  // === OFFRE BOUCLIER 5 QUESTIONS (de-pitched 2026-05-16 — Gemini 3.1 demarketing) ===
  offre: {
    eyebrow: { fr: 'Le Bouclier 5 Questions', en: 'The 5 Questions Shield' },
    title: {
      fr: '7 à 10 jours. 5 à 6 questions. Une protection qui vous suit toute la vie.',
      en: '7 to 10 days. 5 to 6 questions. Coverage that follows you for life.'
    },
    tagline: {
      // Phase 27.66 — split tagline sur \n\n : constat insurance + promesse perso conseillère.
      fr: '« L\'assurance ne guérit pas la maladie. Mais elle vous donne le droit de vous concentrer à 100 % sur votre guérison, sans jamais vous demander si votre famille va devoir payer le prix de votre combat.\n\nC\'est cette paix d\'esprit-là que je vous aide à sécuriser. »',
      en: '"Insurance doesn\'t cure the illness. But it gives you the right to focus 100% on your recovery, without ever wondering if your family will have to pay the price of your fight.\n\nThat\'s the peace of mind I help you secure."'
    },
    // Phase 27.65 — split componentX_desc sur \n\n.
    // Phase 27.67 — tighten ~15% (cuts filler, déjà courts au départ).
    component1_label: { fr: 'Inclus #1', en: 'Included #1' },
    component1_title: { fr: 'Audit gratuit du collectif (45 min)', en: 'Free group-plan audit (45 min)' },
    component1_desc: {
      // Phase 27.75 Codex 2e passe : cut "qui détaille" / "detailing" → ":".
      fr: 'Vous repartez avec un document écrit : ce que votre employeur couvre, et ne couvre pas.\n\nÀ garder. À relire. À partager avec votre conjoint ou vos parents.',
      en: 'You leave with a written document: what your employer covers and does not.\n\nTo keep. To re-read. To share with your partner or parents.'
    },
    component2_label: { fr: 'Inclus #2', en: 'Included #2' },
    component2_title: { fr: 'Guide AMG Québec 2026 (PDF)', en: 'AMG Quebec 2026 guide (PDF)' },
    component2_desc: {
      // Phase 27.75 Codex 2e passe : cut articles "un/an/A/les" devant noms.
      fr: 'Référence familiale : 12 à 16 pages, témoignage anonymisé, chiffres réels du Québec.\n\nLecture libre, sans obligation. Optionnel.',
      en: 'Family reference: 12 to 16 pages, anonymized testimonial, actual Quebec numbers.\n\nFree reading, no obligation. Optional.'
    },
    component3_label: { fr: 'Le cœur du travail', en: 'The heart of the work' },
    component3_title: { fr: '3 soumissions comparatives', en: '3 comparative quotes' },
    component3_desc: {
      fr: 'Manuvie, Canada-Vie, Sun Life. Tableau transparent côte à côte : prime mensuelle, capital, exclusions.\n\nVous voyez exactement ce que vous achetez. Pas de présélection.',
      en: 'Manulife, Canada Life, Sun Life. Transparent side-by-side table: monthly premium, capital, exclusions.\n\nYou see exactly what you\'re buying. No pre-selection.'
    },
    component4_label: { fr: 'Je m\'occupe de tout', en: 'I handle everything' },
    component4_title: { fr: 'Bouclier administratif total', en: 'Full administrative shield' },
    component4_desc: {
      // Phase 27.68 dédup. Phase 27.75 — Codex 2e passe : ponctuation `,` → `.` (staccato).
      fr: 'Tout passe par moi. Pas de questionnaire de 30 pages.\n\nSignature électronique. Aucun déplacement.',
      en: 'Everything goes through me. No 30-page questionnaire.\n\nElectronic signature. No travel.'
    },
    component5_label: { fr: 'On reste en contact', en: 'We stay in touch' },
    component5_title: { fr: 'Suivi annuel à vie', en: 'Annual follow-up for life' },
    component5_desc: {
      // Phase 27.75 Codex 2e passe (EN only) : cut "one is" → "when needed".
      fr: 'Révision chaque année à la date anniversaire. Accompagnement complet le jour d\'une réclamation — vous ne remplissez pas le formulaire seule.\n\nAjustements quand la vie change.',
      en: 'Annual review on your anniversary date. Full claim support when needed — you don\'t fill out the form alone.\n\nAdjustments as life changes.'
    }
  },

  // === TÉMOIGNAGES ===
  communaute: {
    eyebrow: { fr: 'Communauté professionnelle', en: 'Professional community' },
    title: {
      fr: 'Reconnue par mes pairs.',
      en: 'Recognized by my peers.'
    },
    intro: {
      // Phase 27.65 split + Phase 27.67 tighten.
      fr: 'Conseillère à l\'Agence Beaubien (iA Groupe financier, Montréal), 2e place Conseillère-Recrue au Podium iA 2025.\n\nLa confiance se construit aussi entre conseillers, dans un réseau qui veille sur la qualité de la pratique.',
      en: 'Advisor at Beaubien Agency (iA Financial Group, Montréal), 2nd place Rookie Advisor at the iA Podium 2025.\n\nTrust is also built between advisors, in a network that watches over practice quality.'
    },
    photo1_caption: {
      fr: 'Podium iA Groupe financier 2025 · Québec',
      en: 'iA Groupe financier Podium 2025 · Quebec'
    },
    photo2_caption: {
      fr: 'Dîner Podium iA Groupe financier 2025 · Québec',
      en: 'iA Groupe financier Podium 2025 dinner · Quebec'
    }
  },
  testimonials: {
    eyebrow: { fr: 'Témoignages', en: 'Testimonials' },
    title: {
      fr: 'Ce que disent les familles qui ont déjà vu.',
      en: 'What families who have already seen it tell us.'
    },
    disclaimer: {
      fr: 'Extraits de conversations anonymisés, noms, initiales et certains détails (âge, ville) modifiés pour protéger l\'identité. Validés par les personnes concernées. Les versions originales peuvent être partagées en privé, avec leur accord écrit.',
      en: 'Anonymized excerpts from conversations: names, initials and some details (age, city) changed to protect identity. Validated by the people involved. Original versions can be shared privately, with their written permission.'
    },
    t1_initials: 'M.L.',
    t1_role: { fr: 'Mère, 38 ans, Laval', en: 'Mother, 38, Laval' },
    t1_quote: {
      fr: '« Ma sœur a eu un cancer du sein à 31 ans. Quand j\'ai appelé Océanne, elle a vu tout de suite ce que mon collectif ne couvrait pas. Huit jours plus tard, j\'étais protégée. Je dors mieux depuis. »',
      en: '"My sister had breast cancer at 31. When I called Océanne, she saw right away what my group plan was missing. Eight days later, I was covered. I sleep better since."'
    },
    t2_initials: 'É.G.',
    t2_role: { fr: 'Conjointe et mère, 33 ans, Trois-Rivières', en: 'Partner and mother, 33, Trois-Rivières' },
    t2_quote: {
      fr: '« Je ne voulais pas en parler. C\'était trop proche. Océanne ne m\'a pas vendu un produit, elle a écouté, puis elle a posé cinq questions. Aujourd\'hui, mes enfants ont une mère qui dort la nuit. »',
      en: '"I didn\'t want to talk about it. It hit too close. Océanne didn\'t sell me a product, she listened, then she asked five questions. Today, my children have a mother who sleeps at night."'
    },
    t3_initials: 'V.B.',
    t3_role: { fr: 'Cadre, 41 ans, Montréal-Nord', en: 'Manager, 41, Montréal-Nord' },
    t3_quote: {
      fr: '« Mon père a vidé son REER pour ma mère pendant son cancer. Je me suis dit : pas mes enfants. Océanne m\'a accompagnée du premier appel à la signature — sans pression, sans jargon. »',
      en: '"My father emptied his RRSP for my mother during her cancer. I told myself: not my children. Océanne guided me from the first call to the signature, no pressure, no jargon."'
    }
  },

  // === CALCULATRICE AMG (Phase 12) ===
  calc: {
    eyebrow: { fr: 'Calcul indicatif', en: 'Indicative calculation' },
    title: {
      fr: 'Faisons votre chiffre.',
      en: 'Let\'s find your number.'
    },
    intro: {
      fr: 'Quatre chiffres, une minute. Vous obtenez un ordre de grandeur du capital qui collerait à votre vie — pas une moyenne nationale.',
      en: 'Four numbers, one minute. You get a ballpark of the coverage that fits your life — not a national average.'
    },

    // Q1 — Dettes
    q_dettes_label: { fr: 'Ce que vous devez', en: 'What you owe' },
    q_dettes_help: {
      fr: 'Hypothèque et autres dettes que vous voudriez pouvoir couvrir.',
      en: 'Mortgage and other debts you would want covered.'
    },

    // Q2 — Charges mensuelles
    q_charges_label: { fr: 'Ce que coûte votre foyer par mois', en: 'Your household\'s monthly cost' },
    q_charges_help: {
      fr: 'Logement, épicerie, transport, factures — le coût de votre vie.',
      en: 'Housing, groceries, transport, bills — your cost of living.'
    },

    // Q3 — Mois de répit
    q_mois_label: { fr: 'Mois de répit souhaités', en: 'Months of breathing room' },
    q_mois_help: {
      fr: 'Combien de mois vous aimeriez pouvoir tenir sans stress financier.',
      en: 'How many months you\'d want to get through without financial stress.'
    },

    // Q4 — Revenu net qui s'arrêterait
    q_revenu_label: { fr: 'Revenu net qui s\'arrêterait', en: 'Net income that would stop' },
    q_revenu_help: {
      fr: 'Votre revenu net par mois, si vous deviez cesser de travailler. Il illustre la durée que le capital couvrirait — il n\'entre pas dans le calcul du montant.',
      en: 'Your monthly net income, if you had to stop working. It illustrates how long the coverage would last — it is not used to compute the amount.'
    },

    // Result block
    result_label: { fr: 'Capital estimé', en: 'Estimated coverage' },
    result_suffix: { fr: ' $', en: ' CAD' },
    result_note: {
      fr: 'Besoin brut, avant vos protections actuelles et les revenus maintenus du ménage (conjoint, épargne, régime collectif). On l\'ajuste ensemble en audit.',
      en: 'Gross need, before your current coverage and the household\'s maintained income (partner, savings, group plan). We fine-tune it together in the audit.'
    },
    breakdown_title: { fr: 'Comment on arrive là', en: 'How we get there' },

    disclaimer: {
      fr: 'Estimation indicative seulement. Elle ne tient pas compte de vos protections actuelles, de votre régime collectif ni de votre dossier médical. Ne constitue pas un conseil financier personnalisé et n\'offre aucune garantie de résultat. Le montant juste pour vous se valide en audit gratuit. Océanne Brodeur, conseillère en sécurité financière inscrite à l\'AMF, certificat n° 271218.',
      en: 'Indicative estimate only. It does not account for your current coverage, group plan or medical file. It is not personalized financial advice and offers no guarantee of outcome. The right amount for you is confirmed in a free audit. Océanne Brodeur, financial security advisor registered with the AMF, certificate no. 271218.'
    },

    cta_audit: { fr: 'Réserver l\'audit gratuit pour valider', en: 'Book the free audit to confirm' },
    cta_method: { fr: 'Comprendre la méthode', en: 'Understand the method' }
  },

  // === LINKTREE ===
  links: {
    subtitle: {
      fr: 'Conseillère en sécurité financière · Spécialiste AMG · Québec',
      en: 'Financial Security Advisor · Critical Illness Specialist · Quebec'
    },
    tagline: {
      fr: 'Pour les familles qui ont déjà vu le cancer de près',
      en: 'For families who have already seen cancer up close'
    },
    // Phase 27.33-B — Codex audit #2 fix : PDF guide n'existe pas en public/.
    // Label changé en "à paraître" + onClick preventDefault dans le composant
    // pour éviter 404 sur clic. À remplacer par vrai PDF dès qu'Océanne le
    // fournit.
    btn_guide: {
      fr: 'Guide AMG Québec 2026 — à paraître bientôt',
      en: 'AMG Quebec 2026 guide — coming soon'
    },
    btn_audit: { fr: 'Réserver l\'audit gratuit (45 min)', en: 'Book the free audit (45 min)' },
    btn_calc: {
      fr: 'Calculatrice AMG — Quel capital pour ma famille ?',
      en: 'AMG Calculator, How much coverage for my family?'
    },
    btn_infolettre: {
      fr: 'Recevoir l\'infolettre, un courriel par mois, jamais plus',
      en: 'Get the newsletter, one email a month, never more'
    },
    btn_minicours: { fr: 'Mini-cours AMG en 5 vidéos courtes', en: 'AMG mini-course in 5 short videos' },
    btn_articles: { fr: 'Lire les articles du Carnet', en: 'Read the Journal articles' },
    btn_contact: { fr: 'M\'écrire en privé', en: 'Message me privately' },
    footer_disclaimer: {
      fr: '© 2026 Océanne Brodeur · Inscrite AMF · Tous droits réservés',
      en: '© 2026 Océanne Brodeur · Registered with AMF · All rights reserved'
    }
  },

  // === MERCI ===
  merci: {
    eyebrow: { fr: 'Merci', en: 'Thank you' },
    title: {
      fr: 'Votre demande est bien reçue.',
      en: 'Your request is in my hands.'
    },
    body: {
      fr: 'Je vous réponds personnellement sous 24 h ouvrables. Pas de séquence courriel automatique en attendant. Deux ressources si vous voulez creuser — ou rien, c\'est correct aussi.',
      en: 'I reply to you personally within 24 business hours. No automated email sequence in the meantime. Two resources if you want to dig in, or nothing, that\'s fine too.'
    },
    // Phase 27.33-B — PDF à paraître (cf. btn_guide ressources)
    cta_guide: { fr: 'Télécharger mon guide (PDF)', en: 'Download my guide (PDF)' },
    cta_articles: { fr: 'Lire les articles du Carnet', en: 'Read the Journal articles' },
    urgent_note: {
      fr: 'Pour une question urgente, écrivez-moi sur WhatsApp ou appelez-moi au numéro en bas de page.',
      en: 'For an urgent question, message me on WhatsApp or call the number at the bottom of this page.'
    }
  },

  // === LEXIQUE ===
  lexique: {
    eyebrow: { fr: 'Lexique', en: 'Glossary' },
    title: {
      fr: 'Les mots de l\'assurance maladie grave, démystifiés.',
      en: 'The words of critical illness insurance, demystified.'
    },
    intro: {
      fr: 'Voici les mots que l\'industrie tait, et qu\'aucun autre conseiller ne prend le temps de définir devant vous. Démystifier le jargon, pour que vous compreniez exactement ce que vous signez — et ce que vous protégez.',
      en: 'Here are the words the industry won\'t define, and that no other advisor takes the time to walk you through. Demystifying the jargon, so you understand exactly what you sign, and what you protect.'
    },
    // Phase 27.81 — Modal labels (pattern dual modal+page)
    modal_close_label: { fr: 'Fermer le lexique', en: 'Close glossary' },
    modal_print_label: { fr: 'Imprimer le lexique', en: 'Print glossary' },
    modal_cta_full: { fr: 'Lire le lexique complet', en: 'Read the full glossary' },
    term_amg: { fr: 'AMG (Assurance maladie grave)', en: 'AMG (Critical Illness Insurance)' },
    def_amg: {
      fr: 'Protection forfaitaire qui peut verser de 100 000 à 500 000 $ en un seul versement, libre d\'impôt, selon le contrat retenu et les conditions respectées, le jour où un diagnostic couvert tombe. C\'est l\'inverse de l\'invalidité (qui remplace un revenu mois après mois) et de l\'assurance vie (qui se déclenche au décès). Vous décidez comment utiliser l\'argent, médicaments non couverts, hypothèque, congé du conjoint, ou simplement souffler.',
      en: 'Lump-sum coverage that pays $100,000 to $500,000 in a single tax-free payment the day a covered diagnosis arrives. It\'s the opposite of disability (which replaces income month after month) and life insurance (triggered at death). You decide how to use the money, uncovered medication, the mortgage, partner\'s leave, or simply breathing.'
    },
    term_capital: { fr: 'Capital forfaitaire', en: 'Lump-sum capital' },
    def_capital: {
      // 2026-06-11 — « pour la sœur d'une cliente : 28 000 $ » = cas client
      // invérifiable (témoignage) → remplacé par le chiffre sourcé.
      fr: 'Le montant unique versé par l\'assureur après le diagnostic et la période de survie de 30 jours. Pas de remboursement post-justificatifs : c\'est un dépôt direct dans votre compte. Ce qui paie en pratique ? Les dépenses que la RAMQ ne couvre pas, l\'hypothèque, le congé du conjoint — pour le tiers des familles les plus touchées, c\'est près de 1 000 $ par mois à combler (étude nationale, 2021).',
      en: 'The single amount paid by the insurer after diagnosis and the 30-day survival period. No reimbursement on receipts: it\'s a direct deposit into your account. What does it pay for in practice? What RAMQ doesn\'t cover, the mortgage, a partner\'s leave — for the hardest-hit third of families, that\'s nearly $1,000 a month to fill (national study, 2021).'
    },
    term_survie: { fr: 'Période de survie', en: 'Survival period' },
    def_survie: {
      fr: 'Délai standard de 30 jours après le diagnostic, durant lequel l\'assureur attend avant de verser le capital. Si vous êtes toujours en vie au jour 31, l\'argent est déposé. Ce n\'est pas une mise à l\'épreuve, c\'est une borne contractuelle, identique chez les trois grands assureurs canadiens.',
      en: 'Standard 30-day delay after diagnosis, during which the insurer waits before paying the capital. If you\'re still alive on day 31, the money is deposited. It\'s not a test, it\'s a contractual marker, identical across the three major Canadian insurers.'
    },
    term_preexist: { fr: 'Pré-existence', en: 'Pre-existing condition' },
    def_preexist: {
      fr: 'Toute condition médicale connue avant la souscription. Doit être déclarée honnêtement, l\'assureur peut refuser, exclure cette condition spécifique, ou ajuster la prime. Mon conseil : on remplit le questionnaire ensemble, pas tout seule devant l\'écran. Une omission qui semble mineure peut faire annuler la couverture le jour de la réclamation.',
      en: 'Any medical condition known before applying. Must be declared honestly, the insurer may refuse, exclude that specific condition, or adjust the premium. My advice: we fill the questionnaire together, not you alone in front of the screen. A small omission can void coverage exactly on claim day.'
    },
    term_antecedents: { fr: 'Antécédents familiaux', en: 'Family history' },
    def_antecedents: {
      fr: 'Histoire médicale de la famille proche (parents, frères et sœurs). Influence la prime sans toujours être éliminatoire, un parent atteint à 70 ans n\'a pas le même poids qu\'à 40 ans. Et un antécédent positif n\'est pas synonyme de refus : c\'est exactement le genre de nuance que les 3 soumissions comparatives mettent en lumière.',
      en: 'Close family medical history (parents, siblings). Influences premium but is not always disqualifying, a parent affected at 70 doesn\'t carry the same weight as at 40. And a positive history doesn\'t mean refusal: it\'s exactly the kind of nuance the three comparative quotes bring to light.'
    },
    term_soumission: { fr: 'Soumission', en: 'Quote' },
    def_soumission: {
      fr: 'Devis personnalisé d\'un assureur, basé sur votre profil (âge, santé, antécédents). Pour chaque mandat, j\'en demande trois — Manuvie, Canada-Vie, Sun Life — et je les compare devant vous, sans présélection. C\'est rare dans l\'industrie ; la plupart des conseillers vous présentent une seule offre.',
      en: 'Personalized quote from an insurer, based on your profile (age, health, history). For every mandate, I request three: Manulife, Canada Life, Sun Life, and compare them in front of you, with no pre-selection. That\'s rare in the industry; most advisors present a single offer.'
    },
    term_souscription: { fr: 'Souscription', en: 'Application' },
    def_souscription: {
      // 2026-06-11 — « 95 % » non sourçable → « la plupart des profils standards »
      // (r.10 art. 13 : stat écrite = source ; sans source, pas de chiffre).
      fr: 'Le processus de signature finale. Inclut un questionnaire de santé (5 à 6 questions, pas 30 pages) et, parfois, une analyse de sang — la plupart des profils standards sous 250 000 $ de capital n\'en ont pas besoin. Vous ne parlez jamais directement à un assureur, je suis votre seule interlocutrice du dossier.',
      en: 'The final signing process. Includes a health questionnaire (5 to 6 questions, not 30 pages) and, sometimes, lab work — most standard profiles under $250,000 of capital don\'t need it. You never speak to an insurer directly, I\'m your only point of contact on the file.'
    },
    term_collectif: { fr: 'Régime collectif', en: 'Group plan' },
    def_collectif: {
      // 2026-06-11 — « 78 % » introuvable (inventé) → remplacé par le fait structurel
      // vérifié (0,5 % des primes collectives, Groupe Fraser) ; « 6 à 12 mois » faux
      // (l'invalidité longue durée peut courir bien au-delà) → reformulé.
      fr: 'Assurance fournie par votre employeur. Couvre généralement l\'invalidité (60 à 70 % du salaire si vous ne pouvez plus travailler), mais presque jamais la maladie grave comme diagnostic forfaitaire : cette garantie ne représente que 0,5 % des primes d\'assurance collective au Canada (Groupe Fraser). L\'audit gratuit de 45 minutes existe pour ça : on lit votre contrat ensemble, pas un PDF marketing.',
      en: 'Insurance provided by your employer. Generally covers disability (60 to 70% of salary if you can no longer work), but almost never critical illness as a lump-sum diagnosis: that coverage represents only 0.5% of group insurance premiums in Canada (Fraser Group). The free 45-minute audit exists for this: we read your actual contract together, not a marketing PDF.'
    },
    term_loi25: { fr: 'Loi 25', en: 'Law 25' },
    def_loi25: {
      fr: 'Loi québécoise sur la protection des renseignements personnels (en vigueur depuis septembre 2023, équivalent canadien du RGPD européen). L\'article 17 encadre les transferts hors-Québec. Toute donnée que vous me confiez sur ce site est encadrée par cette loi, c\'est pourquoi la case de consentement n\'est pas pré-cochée dans le formulaire : la loi l\'interdit.',
      en: 'Quebec law on personal data protection (in force since September 2023, Canadian equivalent of Europe\'s GDPR). Article 17 governs cross-border transfers. Any data you entrust me with on this site is covered by this law, which is why the consent checkbox on the form isn\'t pre-checked: the law forbids it.'
    },
    term_amf: { fr: 'AMF (Autorité des marchés financiers)', en: 'AMF (Autorité des marchés financiers)' },
    def_amf: {
      fr: 'L\'organisme québécois qui régule les conseillers en sécurité financière et les courtiers en assurance. Toute personne qui vous vend de l\'assurance vie ou maladie au Québec doit y être inscrite, sinon, c\'est illégal. Vous pouvez vérifier mon numéro de certificat directement sur lautorite.qc.ca avant notre rencontre. C\'est votre droit.',
      en: 'The Quebec body that regulates financial security advisors and insurance brokers. Anyone selling life or health insurance in Quebec must be registered with it, otherwise it\'s illegal. You can verify my certificate number directly on lautorite.qc.ca before our meeting. It\'s your right.'
    }
  },

  // === CARNET (RESSOURCES AMG QC — Phase 27.81 refonte) ===
  // NB : eyebrow/title/intro repurposés pour la page ressources.
  // Les clés a1_/a2_/status_coming/estimated_date/cta_unavailable/empty_state
  // restent utilisées par /ressources pour la section "Articles à venir"
  // (sera déplacée vers route dédiée /articles dans sprint futur).
  carnet: {
    eyebrow: { fr: 'Carnet de ressources', en: 'Resource book' },
    title: {
      fr: 'Le carnet — ressources officielles pour comprendre, vérifier, agir.',
      en: 'The book — official resources to understand, verify, and act.'
    },
    intro: {
      fr: 'Toutes les sources officielles pour vérifier mon inscription, comparer les assureurs canadiens, trouver du soutien après un diagnostic, et connaître les programmes gouvernementaux du Québec. Pas un blog, pas un classement marketing — juste les liens directs vers les organismes qui font autorité.',
      en: 'All the official sources to verify my registration, compare Canadian insurers, find support after a diagnosis, and learn about Quebec\'s government programs. Not a blog, not a marketing ranking — just direct links to the organizations that hold the authority.'
    },
    // Phase 27.81 — Modal labels (pattern dual modal+page)
    modal_close_label: { fr: 'Fermer le carnet', en: 'Close address book' },
    modal_print_label: { fr: 'Imprimer le carnet', en: 'Print address book' },
    modal_cta_full: { fr: 'Lire le carnet complet', en: 'Read the full address book' },
    status_coming: { fr: 'À paraître', en: 'Coming soon' },
    cta_unavailable: { fr: 'Lecture bientôt disponible', en: 'Reading available soon' },
    estimated_date: { fr: 'Juin 2026', en: 'June 2026' },
    a1_title: {
      fr: 'Pourquoi votre régime collectif ne vous protège pas contre le cancer',
      en: 'Why your group plan does not protect you against cancer'
    },
    a1_excerpt: {
      fr: 'Votre assurance collective verse 60 % de votre salaire si vous êtes en invalidité. Mais le diagnostic de cancer en lui-même ? Aucun capital. Voici ce que votre contrat dit exactement — et ce qu\'il faut combler avant le jour J.',
      en: 'Your group plan pays 60% of your salary if you go on disability. But the cancer diagnosis itself? No lump sum. Here\'s exactly what your contract says, and what you need to fill in before the day it matters.'
    },
    a1_reading: '5 min',
    a2_title: {
      fr: 'L\'histoire de Marie-Pier : ce que m\'a appris la maladie de ma sœur',
      en: 'Marie-Pier\'s story: what my sister\'s illness taught me'
    },
    a2_excerpt: {
      fr: 'À 32 ans, j\'ai vu ma sœur Sophie y laisser près de 1 000 $ par mois pendant 18 mois (voix composite, voir intro). Ce que j\'ai découvert sur l\'assurance maladie grave a changé ma manière de protéger les miens. Le texte que j\'aurais voulu lire avant.',
      en: 'At 32, I watched my sister Sophie lose nearly $1,000 a month for 18 months (composite voice, see intro). What I learned about critical illness insurance changed how I protect my family. The piece I wish I had read before.'
    },
    a2_reading: '7 min',
    empty_state: {
      fr: 'D\'autres textes arrivent, au rythme des conversations, pas d\'un calendrier marketing. Pour être prévenue dès qu\'un nouveau paraît, l\'infolettre suffit : un courriel par mois, jamais plus.',
      en: 'More pieces are coming, at the pace of conversations, not a marketing calendar. To be notified when a new one is published, the newsletter is enough: one email a month, never more.'
    }
  },

  // === COLOPHON (Phase 27.81 — voix Océanne : typo + palette + principes + équipe + a11y) ===
  colophon: {
    eyebrow: { fr: 'L\'atelier', en: 'The atelier' },
    title: {
      fr: 'Colophon — la méthode derrière les mots et la palette.',
      en: 'Colophon — the method behind the words and the palette.'
    },
    intro: {
      fr: 'Ce site n\'est pas un template. Chaque détail — la typographie, la palette terracotta, la voix éditoriale, l\'accessibilité — résulte d\'un choix. Voici la transparence sur la méthode, les standards et l\'équipe qui ont permis d\'aboutir à ce que vous lisez.',
      en: 'This site is not a template. Every detail — the typography, the terracotta palette, the editorial voice, the accessibility — is the result of a decision. Here is transparency on the method, the standards and the team that brought together what you are reading.'
    },
    // Modal labels (pattern dual modal+page)
    modal_close_label: { fr: 'Fermer le colophon', en: 'Close colophon' },
    modal_print_label: { fr: 'Imprimer le colophon', en: 'Print colophon' },
    modal_cta_full: { fr: 'Lire le colophon complet', en: 'Read the full colophon' }
  },

  // === RESSOURCES (HUB) ===
  ressources: {
    eyebrow: { fr: 'Ressources', en: 'Resources' },
    title: {
      fr: 'Tout ce qu\'il faut pour comprendre — avant de décider.',
      en: 'Everything you need to understand, before you decide.'
    },
    intro: {
      fr: 'Les articles du Carnet, les témoignages, et un guide PDF pour la maison. Trois manières de creuser le sujet — à votre rythme, sans engagement.',
      en: 'The Journal articles, the testimonials, and a PDF guide for the home. Three ways to dig in, at your own pace, no commitment.'
    },
    guide_featured_label: { fr: 'Gratuit · PDF', en: 'Free · PDF' },
    guide_title: { fr: 'Guide AMG Québec 2026', en: 'AMG Quebec 2026 Guide' },
    guide_subtitle: {
      fr: '24 pages, un récit anonymisé, les vrais chiffres du Québec. À garder, à relire, à partager, ou à ignorer. Pas de séquence courriel automatique après le téléchargement.',
      en: '24 pages, an anonymized story, the actual Quebec numbers. To keep, to re-read, to share, or to ignore. No automated email sequence after the download.'
    },
    // Phase 27.33-B — PDF à paraître (cf. btn_guide ressources)
    guide_cta: { fr: 'Télécharger le guide (PDF)', en: 'Download the guide (PDF)' },
    articles_section_title: { fr: 'Articles récents', en: 'Recent articles' },
    articles_view_all: { fr: 'Voir tous →', en: 'View all →' },
    testimonials_section_title: { fr: 'Témoignages', en: 'Testimonials' },
    testimonials_intro: {
      fr: 'Trois voix composites sont présentées sur la page d\'accueil, inspirées de situations récurrentes en pratique, détails modifiés pour protéger l\'identité. Non attribuables à des clients réels.',
      en: 'Three anonymized testimonials are available on the home page, conversation excerpts, details modified to protect identity, validated by the people involved.'
    },
    testimonials_cta: { fr: 'Voir les témoignages →', en: 'View testimonials →' },
    final_section_title: { fr: 'Prête à en parler ?', en: 'Ready to talk?' },
    final_cta: { fr: 'Réserver l\'audit gratuit', en: 'Book the free audit' }
  },

  // === PAGE DE CAPTURE /guide (lead magnet) ===
  guide: {
    eyebrow: { fr: 'Guide gratuit · Québec 2026', en: 'Free guide · Quebec 2026' },
    title: {
      fr: 'Ce que personne ne vous dit sur l\'assurance maladie grave',
      en: 'What no one tells you about critical illness insurance'
    },
    subtitle: {
      fr: 'Le guide des familles québécoises qui ont vu un cancer de près — et qui veulent savoir, avant d\'en avoir besoin, ce que leur régime couvre, et ce qu\'il laisse de côté.',
      en: 'The guide for Quebec families who have seen cancer up close — and want to know, before they need to, what their plan covers, and what it leaves out.'
    },
    inside_label: { fr: 'Ce que vous y trouverez (24 pages)', en: 'What\'s inside (24 pages)' },
    inside_1: {
      fr: 'La différence entre l\'assurance invalidité et l\'assurance maladie grave — la confusion la plus répandue.',
      en: 'The difference between disability and critical illness insurance — the most common confusion.'
    },
    inside_2: {
      fr: 'Ce que votre régime collectif au travail couvre vraiment, et ce qu\'il ne couvre pas.',
      en: 'What your group plan at work really covers, and what it doesn\'t.'
    },
    inside_3: {
      fr: 'Le coût réel d\'un cancer au Québec, chiffres et sources à l\'appui.',
      en: 'The real cost of cancer in Quebec, with figures and sources.'
    },
    inside_4: {
      fr: 'Comment estimer le montant qu\'il vous faudrait, à vous.',
      en: 'How to estimate the amount you would need.'
    },
    inside_5: {
      fr: 'Les 5 questions à poser à vos RH — dès aujourd\'hui.',
      en: 'The 5 questions to ask your HR — starting today.'
    },
    cta: { fr: 'Recevoir mon guide gratuit', en: 'Get my free guide' },
    reassurance: {
      fr: '24 pages d\'information générale, sans engagement. Vous le recevez par courriel. Désabonnement en un clic, quand vous voulez.',
      en: '24 pages of general information, no commitment. Delivered to your inbox. Unsubscribe in one click, anytime.'
    },
    faq_title: { fr: 'Avant de télécharger', en: 'Before you download' },
    faq_q1: { fr: 'Est-ce vraiment gratuit ?', en: 'Is it really free?' },
    faq_a1: {
      fr: 'Oui. Le guide est en lecture libre, sans frais. Mon rôle, c\'est d\'abord d\'informer les familles du Québec.',
      en: 'Yes. The guide is free to read, at no cost. My role is first and foremost to inform Quebec families.'
    },
    faq_q2: { fr: 'Est-ce que je vais me faire harceler au téléphone ?', en: 'Will I get hounded by phone?' },
    faq_a2: {
      fr: 'Non. Vous lisez à votre rythme. C\'est vous qui décidez si vous voulez aller plus loin un jour.',
      en: 'No. You read at your own pace. You decide if and when you want to go further.'
    },
    faq_q3: { fr: 'Pourquoi le donner gratuitement ?', en: 'Why give it away?' },
    faq_a3: {
      fr: 'Parce que la plupart des familles découvrent ces choses-là trop tard. J\'aime autant que vous les sachiez avant.',
      en: 'Because most families learn these things too late. I\'d rather you know them beforehand.'
    },
    faq_q4: {
      fr: 'Quelle est la différence entre l\'assurance invalidité et l\'assurance maladie grave ?',
      en: 'What\'s the difference between disability and critical illness insurance?'
    },
    faq_a4: {
      fr: 'L\'assurance invalidité remplace une partie de votre salaire, mois après mois, si vous ne pouvez plus travailler. L\'assurance maladie grave verse un montant unique et forfaitaire au diagnostic d\'une maladie couverte, à utiliser comme vous voulez — peu importe que vous travailliez ou non. Les deux sont complémentaires.',
      en: 'Disability insurance replaces part of your salary, month after month, if you can no longer work. Critical illness insurance pays a single lump sum upon diagnosis of a covered illness, to use as you wish — whether you work or not. The two are complementary.'
    },
    faq_q5: {
      fr: 'Combien coûte une assurance maladie grave au Québec ?',
      en: 'How much does critical illness insurance cost in Quebec?'
    },
    faq_a5: {
      fr: 'Le prix dépend surtout de votre âge, de votre état de santé et du montant de protection choisi. À titre indicatif, une couverture de 100 000 $ pour une personne dans la trentaine, non-fumeuse, se situe souvent autour de 30 à 40 $ par mois. Le guide donne des ordres de grandeur réels, et l\'audit gratuit permet d\'obtenir votre vrai prix.',
      en: 'The price depends mostly on your age, your health and the coverage amount you choose. As a rough guide, $100,000 of coverage for a non-smoker in their thirties often runs around $30 to $40 a month. The guide gives real ballpark figures, and the free audit gets you your actual price.'
    },
    seo_h2: {
      fr: 'Qu\'est-ce que l\'assurance maladie grave ?',
      en: 'What is critical illness insurance?'
    },
    seo_p1: {
      fr: 'L\'assurance maladie grave — aussi appelée assurance maladies graves — verse un montant forfaitaire, libre d\'impôt, dès le diagnostic d\'une maladie couverte : cancer, infarctus, AVC et une vingtaine d\'autres. Au Québec, elle ne remplace ni votre assurance invalidité ni le régime public : elle s\'ajoute. Elle paie pendant que vous vous soignez, peu importe votre capacité à travailler.',
      en: 'Critical illness insurance pays a tax-free lump sum as soon as you\'re diagnosed with a covered illness: cancer, heart attack, stroke and around twenty others. In Quebec, it replaces neither your disability insurance nor the public plan — it adds to them. It pays while you focus on recovering, no matter your ability to work.'
    },
    seo_p2: {
      fr: 'Combien coûte-t-elle ? En quoi diffère-t-elle de l\'assurance invalidité ? Faut-il la prendre si on est déjà couvert au travail ? Ce guide gratuit répond à ces questions pour les familles du Québec, chiffres et sources à l\'appui.',
      en: 'How much does it cost? How is it different from disability insurance? Do you need it if you\'re already covered at work? This free guide answers these questions for Quebec families, with figures and sources to back them up.'
    },
    credentials: {
      fr: 'Océanne Brodeur, conseillère en sécurité financière · rattachée à iA Groupe financier – Agence Beaubien, elle compare et offre les produits de plusieurs assureurs, sans présélection · Certificat de l\'AMF n° 271218. Document d\'information générale qui ne constitue pas un conseil personnalisé.',
      en: 'Océanne Brodeur, financial security advisor · affiliated with iA Financial Group – Agence Beaubien, she compares and offers products from several insurers, with no pre-selection · AMF certificate no. 271218. General information document that does not constitute personalized advice.'
    },
    cover_alt: { fr: 'Couverture du guide AMG Québec 2026', en: 'Cover of the AMG Quebec 2026 guide' }
  },

  // === MINI-GATE TÉLÉCHARGEMENT (guide lead magnet) ===
  guide_gate: {
    email_placeholder: { fr: 'Votre courriel', en: 'Your email' },
    consent: {
      fr: 'J\'accepte de recevoir le guide et l\'infolettre d\'Océanne Brodeur. Je peux me désabonner à tout moment.',
      en: 'I agree to receive the guide and Océanne Brodeur\'s newsletter. I can unsubscribe anytime.'
    },
    cta: { fr: 'Télécharger le guide', en: 'Download the guide' },
    submitting: { fr: 'Un instant…', en: 'One moment…' },
    success: { fr: 'Merci ! Votre guide se télécharge.', en: 'Thank you! Your guide is downloading.' },
    success_link: { fr: 'Le téléchargement n\'a pas démarré ? Cliquez ici.', en: 'Download didn\'t start? Click here.' },
    err_email: { fr: 'Courriel invalide.', en: 'Invalid email.' },
    err_consent: { fr: 'Cochez la case pour recevoir le guide.', en: 'Please check the box to receive the guide.' },
    err_generic: { fr: 'Oups — réessayez dans un instant.', en: 'Oops — please try again in a moment.' }
  },

  // === FAQ ===
  faq: {
    eyebrow: { fr: 'Vos questions, mes réponses', en: 'Your questions, my answers' },
    title: { fr: 'Questions fréquentes', en: 'Frequently asked questions' },
    q1: {
      fr: 'Qu\'est-ce que l\'assurance maladie grave et comment fonctionne-t-elle ?',
      en: 'What is critical illness insurance, and how does it work?'
    },
    a1: {
      fr: 'C\'est une protection forfaitaire. Le jour où un diagnostic couvert tombe : cancer, AVC, infarctus, sclérose en plaques et une vingtaine d\'autres conditions, et que vous survivez 30 jours, l\'assureur dépose un capital unique de 100 000 à 500 000 $ dans votre compte, libre d\'impôt. L\'argent vous appartient. Vous décidez : médicaments non couverts, hypothèque, congé non payé du conjoint, ou simplement souffler. À ne pas confondre avec l\'assurance invalidité (qui remplace un pourcentage de salaire mois après mois) ni avec l\'assurance vie (qui se déclenche au décès). L\'assurance maladie grave vous paie, de votre vivant.',
      en: 'It\'s lump-sum coverage. The day a covered diagnosis arrives: cancer, stroke, heart attack, multiple sclerosis, plus around twenty other conditions, and you survive 30 days, the insurer deposits a single tax-free lump sum of $100,000 to $500,000 into your account. The money is yours. You decide: uncovered medication, the mortgage, your partner\'s unpaid leave, or simply breathing. Not to be confused with disability insurance (which replaces a percentage of salary month after month) or life insurance (triggered at death). Critical illness pays you, while you\'re alive.'
    },
    q2: {
      fr: 'Pourquoi souscrire si j\'ai déjà une assurance collective ?',
      en: 'Why take this on if I already have group insurance?'
    },
    a2: {
      // 2026-06-11 — « 90 % des cas » non sourcé + « plafonné à quelques mois » faux
      // (l'invalidité LD peut courir des années) + « chimio expérimentale » trompeur
      // → recadré sur le vérifié (0,5 % des primes collectives = AMG, Groupe Fraser).
      fr: 'Permettez-moi de vous demander une chose : votre régime collectif couvre-t-il la maladie grave comme diagnostic, ou seulement l\'invalidité qui en résulte ? C\'est deux choses différentes. Le collectif paie typiquement 60 à 70 % du salaire pendant l\'invalidité — un revenu partiel si vous ne pouvez plus travailler — pas un capital forfaitaire qui paie les dépenses que la RAMQ ne couvre pas, les transports, l\'hypothèque et les semaines où votre conjoint accompagne. L\'assurance maladies graves ne représente d\'ailleurs que 0,5 % des primes d\'assurance collective au Canada (Groupe Fraser). Apportez votre contrat à notre rencontre, on regardera ensemble. La plupart des gens découvrent un trou qu\'ils n\'avaient pas vu.',
      en: 'Let me ask you one thing: does your group plan cover critical illness as a diagnosis, or only the disability that results from it? Those are two different things. Group plans typically pay 60 to 70% of salary during disability — partial income if you can no longer work — not a lump sum that pays for what RAMQ doesn\'t cover, transport, the mortgage and the weeks your partner attends treatment. Critical illness coverage actually represents only 0.5% of group insurance premiums in Canada (Fraser Group). Bring your contract to our meeting, we\'ll read it together. Most people find a gap they hadn\'t seen.'
    },
    q3: {
      fr: 'Quels types de maladies sont couvertes ?',
      en: 'What types of illnesses are covered?'
    },
    a3: {
      fr: 'Les trois grandes en tête : cancer (toutes formes admissibles selon la définition contractuelle), accident vasculaire cérébral, infarctus du myocarde. Puis une vingtaine d\'autres : sclérose en plaques, Parkinson, Alzheimer précoce, insuffisance rénale terminale, transplantation d\'organe majeur, paralysie, cécité, surdité, brûlures sévères. Chaque assureur (Manuvie, Canada-Vie, Sun Life) a sa propre liste, et ses propres définitions contractuelles, parfois sensiblement différentes. C\'est exactement pour ça que je compare trois soumissions côte à côte avant que vous signiez quoi que ce soit.',
      en: 'The three big ones first: cancer (all forms eligible per the contractual definition), stroke, heart attack. Then around twenty more: multiple sclerosis, Parkinson\'s, early-onset Alzheimer\'s, end-stage kidney failure, major organ transplant, paralysis, blindness, deafness, severe burns. Each insurer (Manulife, Canada Life, Sun Life) has its own list, and its own contractual definitions, sometimes meaningfully different. That\'s exactly why I compare three quotes side by side before you sign anything.'
    },
    q4: {
      fr: 'Est-ce que je peux protéger mon conjoint et mes enfants aussi ?',
      en: 'Can I cover my partner and children too?'
    },
    a4: {
      fr: 'Oui, et c\'est souvent plus pertinent qu\'une police individuelle, surtout pour les jeunes familles. Votre conjoint peut être couvert en police séparée ou en formule couple, avec un capital équivalent ou ajusté. Et chez la plupart des assureurs, l\'avenant enfants est inclus gratuitement ou pour un coût symbolique : il couvre tous vos enfants présents et à naître. Les conditions couvertes incluent cancers pédiatriques, malformations cardiaques congénitales, diabète de type 1, et plusieurs autres. On en discute pendant l\'audit — pas avant.',
      en: 'Yes, and it\'s often more relevant than an individual policy, especially for young families. Your partner can be covered on a separate policy or a couple plan, with equivalent or adjusted capital. And with most insurers, the children\'s rider is included free or at a token cost: it covers all your children, present and future. Covered conditions include pediatric cancers, congenital heart conditions, type 1 diabetes, and several others. We discuss this during the audit, not before.'
    },
    q5: {
      fr: 'Que se passe-t-il si je reçois un diagnostic ? Comment le montant est-il versé ?',
      en: 'What happens if I receive a diagnosis? How is the amount paid?'
    },
    a5: {
      fr: 'Vous m\'appelez. Je remplis la réclamation avec vous, pas vous seule devant un formulaire de 12 pages. L\'assureur reçoit le diagnostic médical, vérifie qu\'il correspond à une condition couverte au contrat, et vous attendez la période de survie de 30 jours (standard de l\'industrie). Au jour 31, le capital forfaitaire est déposé dans votre compte bancaire, en un seul versement, libre d\'impôt. Aucune justification à fournir sur l\'usage. Cet argent paie ce que vous décidez, traitement, congé du conjoint, voyage de répit, hypothèque, peu importe.',
      en: 'You call me. I file the claim with you, not you alone in front of a 12-page form. The insurer receives the medical diagnosis, confirms it matches a covered condition in the contract, and you wait the 30-day survival period (industry standard). On day 31, the lump-sum capital is deposited in your bank account, in a single payment, tax-free. No justification needed on how you use it. That money pays for what you decide: treatment, partner\'s leave, respite trip, mortgage, anything.'
    },
    q6: {
      fr: 'Est-ce que je dois faire une prise de sang ?',
      en: 'Do I need a blood test?'
    },
    // 2026-06-11 — « 95 % » non sourçable → « la plupart » (r.10 art. 13).
    a6: {
      fr: 'Pour la plupart des profils standards sous 250 000 $ de capital : non. 5 à 6 questions de santé suffisent, antécédents familiaux, médicaments actuels, hospitalisations récentes, tabagisme, voyages à risque. Pour les capitaux plus élevés (300 000 $ et plus), l\'assureur peut demander une analyse, mais c\'est de plus en plus rare grâce aux nouveaux modèles de souscription simplifiée. Je vous dis exactement où vous tombez avant qu\'on choisisse le capital — pas après.',
      en: 'For most standard profiles under $250,000 of capital: no. 5 to 6 health questions are enough, family history, current medications, recent hospitalizations, smoking, high-risk travel. For higher capitals ($300,000 and up), the insurer may request lab work, but this is increasingly rare thanks to new simplified underwriting models. I tell you exactly where you fall before we settle on the capital, not after.'
    }
  },

  // === OBJECTIONS (réponses verbatim brief CH.7) ===
  objections: {
    obj1_q: { fr: 'J\'ai déjà une assurance vie. Ça devrait suffire.', en: 'I already have life insurance. That should be enough.' },
    obj1_a: {
      fr: '« L\'assurance vie protège vos proches après votre décès. L\'assurance maladie grave vous protège, vous, pendant que vous luttez pour guérir. Ce n\'est pas le même produit, ni la même crise. »',
      en: '"Life insurance protects your loved ones after your death. Critical illness insurance protects you while you\'re fighting to heal. Not the same product, not the same crisis."'
    },
    obj2_q: { fr: 'Mon employeur me couvre déjà.', en: 'My employer already covers me.' },
    // 2026-06-11 — « 90 % des cas » non sourcé + « chimio expérimentale » trompeur
    // → recadré (le fait structurel : AMG = 0,5 % des primes collectives, Groupe Fraser).
    obj2_a: {
      fr: '« Permettez-moi de vous poser une question : votre régime collectif couvre-t-il la maladie grave comme diagnostic, ou seulement l\'invalidité qui en résulte ? Presque toujours, c\'est la deuxième réponse — l\'assurance maladies graves ne représente que 0,5 % des primes collectives au Canada (Groupe Fraser). Et 60 % du salaire ne couvre pas ce que la RAMQ ne rembourse pas. »',
      en: '"Let me ask you one thing: does your group plan cover critical illness as a diagnosis, or only the disability that results from it? Almost always, it\'s the second answer — critical illness coverage represents only 0.5% of group premiums in Canada (Fraser Group). And 60% of your salary doesn\'t cover what RAMQ doesn\'t reimburse."'
    },
    obj3_q: { fr: 'C\'est trop cher pour quelque chose qui n\'arrivera peut-être pas.', en: 'It\'s too expensive for something that might not happen.' },
    // 2026-06-11 — « 60 $/mois pour 250 000 $ » sous-estimait le prix réel (≈70-95 $,
    // PolicyAdvisor/PolicyMe) = risque « miroiter » r.10 art. 14 → ancré sur le vérifié.
    obj3_a: {
      fr: '« Au Canada, deux personnes sur cinq développeront un cancer dans leur vie. À 30-35 ans, non-fumeur, 100 000 $ de capital coûte typiquement 30 à 40 $ par mois — souvent moins cher que votre forfait cellulaire. Votre vrai prix : une soumission personnalisée. »',
      en: '"In Canada, two in five people will develop cancer in their lifetime. At 30-35, non-smoker, $100,000 of capital typically costs $30 to $40 a month — often less than your phone plan. Your real price: a personalized quote."'
    },
    obj4_q: { fr: 'Je vais y penser et revenir.', en: 'I\'ll think about it and come back.' },
    obj4_a: {
      fr: '« Voici ce que je vous propose : prenez 15 minutes pour télécharger le guide AMG. Lisez-le tranquillement. Mais ne reportez pas la décision jusqu\'à un diagnostic, c\'est à ce moment-là qu\'il sera trop tard pour souscrire. »',
      en: '"Here\'s what I suggest: take 15 minutes to download the AMG guide. Read it at your own pace. But don\'t push the decision until a diagnosis, by then it will be too late to apply."'
    }
  },

  // === FORMULAIRE CONTACT (audit gratuit) ===
  form: {
    eyebrow: { fr: 'Parlons de votre situation', en: 'Let\'s talk about your situation' },
    title: {
      fr: 'Réservez votre audit gratuit du collectif (45 min)',
      en: 'Book your free group-plan audit (45 min)'
    },
    guarantee_title: {
      fr: 'Mon engagement',
      en: 'My commitment'
    },
    guarantee_body: {
      fr: '45 minutes. Audit gratuit. Sans engagement. Si une assurance maladie grave n\'est pas pertinente pour votre situation, je vous le dis. Vous repartez avec un document écrit dans tous les cas.',
      en: '45 minutes. Free audit. No commitment. If critical illness insurance is not right for your situation, I tell you. You leave with a written document either way.'
    },
    label_first_name: { fr: 'Prénom', en: 'First name' },
    label_last_name: { fr: 'Nom', en: 'Last name' },
    label_email: { fr: 'Courriel', en: 'Email' },
    label_phone: { fr: 'Téléphone', en: 'Phone' },
    // Phase 27.84 — Meta Lead Ads parity : 2 qualifying questions (optional)
    label_age_range: { fr: 'Tranche d\'âge (optionnel)', en: 'Age range (optional)' },
    label_family_status: { fr: 'Situation familiale', en: 'Family situation' },
    select_default: { fr: '— Sélectionner —', en: '— Select —' },
    age_25_35: { fr: '25-35 ans', en: '25-35 years' },
    age_35_45: { fr: '35-45 ans', en: '35-45 years' },
    age_45_55: { fr: '45-55 ans', en: '45-55 years' },
    age_55_65: { fr: '55 ans et +', en: '55 and over' },
    family_with_children: { fr: 'Famille avec enfants', en: 'Family with children' },
    couple_no_children: { fr: 'Couple sans enfants', en: 'Couple without children' },
    family_single_parent: { fr: 'Monoparental(e)', en: 'Single parent' },
    family_single: { fr: 'Célibataire', en: 'Single' },
    label_message: { fr: 'Votre message (optionnel)', en: 'Your message (optional)' },
    placeholder_message: {
      fr: 'Quelques mots sur ce qui vous amène, sans détails médicaux. Aucune pression : c\'est votre rythme.',
      en: 'A few words about what brings you here, no medical details needed. No pressure: it\'s your pace.'
    },
    consent_loi25: {
      fr: 'J\'accepte qu\'Océanne Brodeur recueille et utilise mes renseignements personnels pour me recontacter, et que mes coordonnées soient transmises sous forme chiffrée à Meta afin de mesurer l\'efficacité des publicités, conformément à la Loi 25 du Québec.',
      en: 'I agree that Océanne Brodeur may collect and use my personal information to contact me, and that my contact details be shared in hashed form with Meta to measure ad effectiveness, per Quebec\'s Law 25.'
    },
    submit: { fr: 'Réserver mon audit gratuit', en: 'Book my free audit' },
    submitting: { fr: 'Envoi en cours…', en: 'Sending…' },
    success: {
      fr: 'Reçu. Je vous réponds personnellement dans la journée — pas de relance robotique, pas de séquence automatisée.',
      en: 'Received. I\'ll reply to you personally within the day, no robotic follow-up, no automated sequence.'
    },
    error: {
      fr: 'Une erreur est survenue de notre côté. Réessayez dans un instant, ou écrivez-moi directement à contact@oceanne-brodeur.com.',
      en: 'Something went wrong on our end. Try again in a moment, or write to me directly at contact@oceanne-brodeur.com.'
    },
    required_field: { fr: 'Champ requis', en: 'Required field' },
    // Phase 27.10 — bilingual error keys for inline form validation
    error_email_invalid: { fr: 'Courriel invalide', en: 'Invalid email' },
    error_phone_short: { fr: 'Téléphone trop court', en: 'Phone number too short' },
    error_consent_required: { fr: 'Consentement Loi 25 requis', en: 'Law 25 consent required' }
  },

  // === COOKIE BANNER (LOI 25 — témoins) ===
  cookie: {
    title: { fr: 'Vos données, votre choix', en: 'Your data, your choice' },
    message: {
      fr: 'J\'utilise des témoins pour améliorer votre expérience et mesurer la performance du site, conformément à la Loi 25 du Québec. Vous pouvez accepter, refuser ou personnaliser à tout moment.',
      en: 'I use cookies to improve your experience and measure site performance, per Quebec\'s Law 25. You can accept, decline or customize at any time.'
    },
    accept: { fr: 'Tout accepter', en: 'Accept all' },
    reject: { fr: 'Refuser', en: 'Decline' },
    customize: { fr: 'Personnaliser', en: 'Customize' },
    privacy_link: { fr: 'Politique de confidentialité', en: 'Privacy policy' }
  },

  // === FOOTER ===
  footer: {
    tagline: {
      fr: 'Spécialiste 100 % AMG · Inscrite AMF · Québec',
      en: '100% critical illness · AMF registered · Quebec'
    },
    contact_title: { fr: 'Contact', en: 'Contact' },
    contact_email_label: { fr: 'Courriel', en: 'Email' },
    contact_phone_label: { fr: 'Téléphone', en: 'Phone' },
    nav_title: { fr: 'Navigation', en: 'Navigation' },
    legal_title: { fr: 'Mentions légales', en: 'Legal' },
    disclaimer_amf: {
      fr: 'Inscrite à l\'Autorité des marchés financiers du Québec · Certificat n° 271218',
      en: 'Registered with the Autorité des marchés financiers of Quebec · Certificate no. 271218'
    },
    disclaimer_rendement: {
      fr: 'Les garanties, conditions et exclusions dépendent du produit et de l\'assureur retenus. Aucune garantie de résultat. Lisez attentivement votre contrat avant signature.',
      en: 'Guarantees, conditions and exclusions depend on the chosen product and insurer. No guarantee of outcome. Read your contract carefully before signing.'
    },
    copyright: { fr: '© 2026 Océanne Brodeur. Tous droits réservés.', en: '© 2026 Océanne Brodeur. All rights reserved.' },
    mentions: { fr: 'Mentions légales', en: 'Legal notices' },
    privacy: { fr: 'Confidentialité', en: 'Privacy' },
    accessibility: { fr: 'Accessibilité', en: 'Accessibility' }
  },

  // === MENTIONS LÉGALES / POLITIQUE CONFIDENTIALITÉ (placeholders pages dédiées) ===
  legal: {
    mentions_title: { fr: 'Mentions légales', en: 'Legal notices' },
    mentions_body: { fr: 'Éditeur, hébergeur, inscription AMF et propriété intellectuelle, voir la page Mentions légales complète.', en: 'Publisher, host, AMF registration and intellectual property. See the full Legal notices page.' },
    privacy_title: { fr: 'Politique de confidentialité', en: 'Privacy policy' },
    privacy_body: { fr: 'Loi 25 du Québec, données collectées, finalités, conservation, vos droits et contact du responsable, voir la page Confidentialité complète.', en: 'Quebec\'s Law 25, data collected, purposes, retention, your rights and data controller contact. See the full Privacy page.' },
    accessibility_title: { fr: 'Déclaration d\'accessibilité', en: 'Accessibility statement' },
    accessibility_body: { fr: 'Engagement WCAG 2.2 AA, point de contact pour signaler une barrière, alternatives sur demande.', en: 'WCAG 2.2 AA commitment, contact point to report a barrier, alternatives available on request.' }
  },

  // === MENTIONS LÉGALES ===
  mentions: {
    eyebrow: { fr: 'Mentions légales', en: 'Legal notices' },
    title: { fr: 'Mentions légales', en: 'Legal notices' },
    last_updated: { fr: 'Dernière mise à jour : 2026-05-15', en: 'Last updated: 2026-05-15' },
    s1_title: { fr: 'Identification', en: 'Identification' },
    s1_body: {
      fr: 'Océanne Brodeur, conseillère en sécurité financière inscrite à l\'Autorité des marchés financiers (AMF) du Québec, certificat n° 271218. Cabinet d\'attache : iA Groupe financier — Agence Beaubien, 1611, boul. Crémazie Est, bureau 501, Montréal (Québec), H2M 2R9.',
      en: 'Océanne Brodeur, financial security advisor registered with the Autorité des marchés financiers (AMF) of Quebec, certificate no. 271218. Affiliated firm: iA Financial Group — Beaubien Agency, 1611 boul. Crémazie Est, suite 501, Montréal (Québec) H2M 2R9.'
    },
    s2_title: { fr: 'Conditions d\'utilisation', en: 'Terms of use' },
    s2_body: {
      fr: 'Le contenu de ce site est fourni à titre informatif et éducatif. Aucune information ne constitue un conseil financier personnalisé ni une offre d\'assurance. Pour un audit gratuit et un avis adapté à votre situation, écrivez-moi directement.',
      en: 'Content on this site is provided for informational and educational purposes only. No information constitutes personalized financial advice nor an insurance offer. For a free audit and advice tailored to your situation, write to me directly.'
    },
    s3_title: { fr: 'Propriété intellectuelle', en: 'Intellectual property' },
    s3_body: {
      fr: 'L\'ensemble du contenu de ce site (textes, images, design, code) est protégé par le droit d\'auteur canadien. Toute reproduction, totale ou partielle, sans autorisation écrite préalable est interdite.',
      en: 'All content on this site (text, images, design, code) is protected by Canadian copyright. Any reproduction, in whole or in part, without prior written permission is prohibited.'
    },
    s4_title: { fr: 'Limitation de responsabilité', en: 'Limitation of liability' },
    s4_body: {
      fr: 'Je m\'engage à offrir une information fiable et à jour, mais l\'exactitude absolue ne peut être garantie. L\'assurance maladie grave est un produit complexe, la décision finale appartient à la cliente, après audit personnalisé et lecture du contrat.',
      en: 'I am committed to providing reliable and current information, but absolute accuracy cannot be guaranteed. Critical illness insurance is a complex product, the final decision rests with the client, after a personalized audit and a careful reading of the contract.'
    },
    s5_title: { fr: 'Hébergement', en: 'Hosting' },
    s5_body: {
      fr: 'Site hébergé sur l\'infrastructure Cloudflare Workers (États-Unis). Les données que vous me transmettez sont soumises à la Loi 25 du Québec, voir la Politique de confidentialité pour les détails.',
      en: 'Site hosted on Cloudflare Workers infrastructure (United States). Data you transmit to me is subject to Quebec\'s Law 25, see the Privacy Policy for details.'
    },
    s6_title: { fr: 'Contact', en: 'Contact' },
    s6_body: {
      fr: 'Pour toute question légale, écrivez-moi à contact@oceanne-brodeur.com.',
      en: 'For any legal questions, write to me at [email TBD].'
    }
  },

  // === CONFIDENTIALITÉ (LOI 25 — 8 articles couverts: 8/9/10/15/16/17/19/22) ===
  privacy: {
    eyebrow: { fr: 'Confidentialité', en: 'Privacy' },
    title: { fr: 'Politique de confidentialité', en: 'Privacy policy' },
    last_updated: { fr: 'Dernière mise à jour : 2026-06-08', en: 'Last updated: 2026-06-08' },
    intro: {
      fr: 'Je respecte votre vie privée et applique strictement la Loi 25 du Québec sur la protection des renseignements personnels (en vigueur depuis septembre 2023).',
      en: 'I respect your privacy and strictly apply Quebec\'s Law 25 on personal information protection (in force since September 2023).'
    },
    // Art. 9 — Finalités de la collecte
    s1_title: { fr: 'Données collectées et finalités (Art. 9)', en: 'Data collected and purposes (Art. 9)' },
    s1_body: {
      fr: 'Quand vous soumettez un formulaire, je collecte : prénom, nom, courriel, téléphone, message libre. Témoins techniques (langue, consentement) ; témoins analytiques ou marketing uniquement avec votre consentement explicite par catégorie. Les données servent exclusivement à : (1) vous contacter pour fixer un audit gratuit, (2) suivi commercial post-audit pendant 24 mois maximum, (3) statistiques anonymisées d\'usage du site si vous y avez consenti, (4) mesure de l\'efficacité de nos publicités : si vous cochez la case de consentement du formulaire, vos coordonnées (courriel, téléphone, nom) sont transmises à Meta sous forme chiffrée et non lisible pour évaluer le rendement des campagnes — le contenu de votre message n\'est jamais transmis.',
      en: 'When you submit a form, I collect: first name, last name, email, phone, free-form message. Technical cookies (language, consent); analytics or marketing cookies only with your explicit per-category consent. The data is used exclusively to: (1) contact you to schedule a free audit, (2) commercial follow-up post-audit for 24 months maximum, (3) anonymized site usage statistics if you consented, (4) measuring our ad effectiveness: if you check the form consent box, your contact details (email, phone, name) are shared with Meta in hashed, unreadable form to assess campaign performance — your message content is never shared.'
    },
    // Art. 10 — Catégories de personnes ayant accès
    s2_title: { fr: 'Personnes ayant accès aux données (Art. 10)', en: 'Persons with access to data (Art. 10)' },
    s2_body: {
      fr: 'Accès interne : Océanne Brodeur (conseillère en sécurité financière) et son équipe technique restreinte (Intralys, opérateur du site). Accès externe : les sous-traitants listés à la section « Communication à des tiers » ci-dessous, ainsi que Meta (mesure publicitaire, coordonnées chiffrées) si vous avez coché la case de consentement du formulaire. En dehors de ce cas, aucune donnée n\'est partagée avec des partenaires commerciaux à des fins publicitaires sans votre consentement explicite.',
      en: 'Internal access: Océanne Brodeur (financial security advisor) and her restricted technical team (Intralys, site operator). External access: the subcontractors listed in the "Communication to third parties" section below, plus Meta (ad measurement, hashed contact details) if you checked the form consent box. Apart from this case, no data is shared with commercial partners for advertising purposes without your explicit consent.'
    },
    // Art. 16 — Conservation
    s3_title: { fr: 'Conservation (Art. 16)', en: 'Retention (Art. 16)' },
    s3_body: {
      fr: '24 mois après le dernier contact, selon notre procédure interne de revue périodique des données. Vous pouvez demander la suppression à tout moment via le formulaire de contact ou directement par courriel. Les journaux techniques (audit, sécurité) sont conservés pour une durée maximale de 12 mois.',
      en: '24 months after last contact, according to our internal periodic data review procedure. You may request deletion at any time via the contact form or direct email. Technical logs (audit, security) are kept for a maximum of 12 months.'
    },
    // Art. 19 — Droits d'accès/rectification
    s4_title: { fr: 'Vos droits (Loi 25, Art. 19)', en: 'Your rights (Law 25, Art. 19)' },
    s4_body: {
      fr: 'Vous avez le droit d\'accéder à vos données, de les rectifier, de les supprimer ou de les porter. Vous pouvez également retirer votre consentement à tout moment via le bouton « Modifier mon consentement » en bas de cette page. Délai de réponse : 30 jours maximum à compter de la réception de votre demande.',
      en: 'You have the right to access your data, rectify it, delete it or port it. You may also withdraw your consent at any time via the "Modify my consent" button at the bottom of this page. Response time: 30 days maximum from receipt of your request.'
    },
    // Art. 17 — Transferts hors-Québec
    s5_title: { fr: 'Transferts hors-Québec (Art. 17)', en: 'Cross-border transfers (Art. 17)' },
    s5_body: {
      fr: 'Vos données peuvent être transférées vers des serveurs situés aux États-Unis : Cloudflare Workers (hébergement du site, données techniques en transit), GHL CRM / LeadConnector (gestion des contacts et suivi commercial), et Meta Platforms (mesure de l\'efficacité publicitaire — coordonnées transmises sous forme chiffrée, uniquement si vous avez coché la case de consentement du formulaire). Une évaluation des facteurs relatifs à la vie privée (EFVP) est réalisée pour chaque sous-traitant hors-Québec, conformément à l\'article 17 de la Loi 25. Ces transferts sont sécurisés par chiffrement TLS et encadrés par les clauses contractuelles de chaque sous-traitant. C\'est pourquoi je vous demande votre consentement explicite via la case Loi 25 sur le formulaire avant tout envoi.',
      en: 'Your data may be transferred to servers located in the United States: Cloudflare Workers (site hosting, technical data in transit), GHL CRM / LeadConnector (contact management and commercial follow-up), and Meta Platforms (ad effectiveness measurement — contact details shared in hashed form, only if you checked the form consent box). A Privacy Impact Assessment (PIA / EFVP) is conducted for each non-Quebec subcontractor, in compliance with Article 17 of Law 25. These transfers are secured by TLS encryption and framed by each subcontractor\'s contractual clauses. This is why I ask for your explicit consent via the Law 25 checkbox on the form before any transmission.'
    },
    // Art. 22 — Cookies/témoins
    s6_title: { fr: 'Témoins (cookies) — Art. 22', en: 'Cookies (témoins) — Art. 22' },
    s6_body: {
      fr: 'Trois catégories de témoins, avec consentement granulaire par catégorie via le bandeau d\'accueil et le bouton « Modifier mon consentement » : (1) Essentiels : langue, consentement, formulaire (toujours actifs, sans consentement requis). (2) Analytiques — mesure de fréquentation anonymisée (opt-in explicite). (3) Marketing — Meta Pixel, Google Ads (opt-in explicite, désactivés tant que non configurés).',
      en: 'Three cookie categories, with granular per-category consent via the welcome banner and "Modify my consent" button: (1) Essential: language, consent, form (always active, no consent required). (2) Analytics, anonymized traffic measurement (explicit opt-in). (3) Marketing, Meta Pixel, Google Ads (explicit opt-in, disabled until configured).'
    },
    // Art. 15 — Communication aux tiers (sous-traitants)
    s7_title: { fr: 'Communication à des tiers (Art. 15)', en: 'Communication to third parties (Art. 15)' },
    s7_body: {
      fr: 'Liste exhaustive des sous-traitants ayant accès aux données : Cloudflare (hébergement Workers + base D1 audit, États-Unis) ; GHL / LeadConnector (CRM commercial, États-Unis) ; Resend (envoi de courriels transactionnels, États-Unis) ; Calendly (prise de rendez-vous en ligne, États-Unis) ; Meta Platforms (mesure de l\'efficacité des publicités, coordonnées sous forme chiffrée, États-Unis — uniquement avec votre consentement via la case du formulaire). Chaque sous-traitant est lié par un contrat conforme à la Loi 25. Aucune autre communication à un tiers n\'a lieu sans votre consentement explicite, sauf obligation légale (mandat judiciaire).',
      en: 'Complete list of subcontractors with access to data: Cloudflare (Workers hosting + D1 audit database, USA); GHL / LeadConnector (commercial CRM, USA); Resend (transactional email delivery, USA); Calendly (online appointment booking, USA); Meta Platforms (ad effectiveness measurement, hashed contact details, USA — only with your consent via the form checkbox). Each subcontractor is bound by a Law 25-compliant contract. No other communication to a third party occurs without your explicit consent, except legal obligation (court order).'
    },
    // Art. 8 — Identification du responsable
    s8_title: { fr: 'Responsable du traitement (Art. 8) — contact DPO', en: 'Data controller (Art. 8) — DPO contact' },
    s8_body: {
      fr: 'Responsable du traitement : Océanne Brodeur, conseillère en sécurité financière inscrite à l\'Autorité des marchés financiers (AMF). Adresse : Québec. Pour toute question relative à vos données ou pour exercer vos droits, écrivez à : contact@oceanne-brodeur.com. Déléguée à la protection des données (DPO) : Océanne Brodeur, joignable à la même adresse. Vous pouvez également déposer une plainte auprès de la Commission d\'accès à l\'information du Québec (cai.gouv.qc.ca).',
      en: 'Data controller: Océanne Brodeur, financial security advisor registered with the Autorité des marchés financiers (AMF). Address: Quebec. For any question regarding your data or to exercise your rights, write to: contact@oceanne-brodeur.com. Data protection officer (DPO): Océanne Brodeur, reachable at the same address. You may also file a complaint with the Quebec Commission d\'accès à l\'information (cai.gouv.qc.ca).'
    }
  },

  // === ERROR / SYSTEM PAGES ===
  errors: {
    not_found_title: { fr: 'Page introuvable', en: 'Page not found' },
    not_found_body: {
      fr: 'Cette page n\'existe pas — ou plus. Revenons à l\'essentiel.',
      en: 'This page doesn\'t exist, or no longer does. Let\'s get back to what matters.'
    },
    back_home: { fr: 'Retour à l\'accueil', en: 'Back to home' }
  },

  // === A11Y / SKIP LINKS ===
  a11y: {
    skip_to_content: { fr: 'Aller au contenu principal', en: 'Skip to main content' },
    menu_open: { fr: 'Ouvrir le menu', en: 'Open menu' },
    menu_close: { fr: 'Fermer le menu', en: 'Close menu' },
    lang_switcher: { fr: 'Choisir la langue', en: 'Choose language' }
  },

  // === COOKIES / CONSENT (LOI 25 — granular) ===
  cookies: {
    aria_label: { fr: 'Consentement aux témoins', en: 'Cookie consent' },
    intro: {
      fr: 'Nous utilisons des témoins pour améliorer votre expérience et respecter la Loi 25.',
      en: 'We use cookies to improve your experience and comply with Quebec\'s Law 25.'
    },
    policy_link: { fr: 'Politique de confidentialité', en: 'Privacy policy' },
    customize: { fr: 'Personnaliser', en: 'Customize' },
    reject: { fr: 'Refuser', en: 'Reject all' },
    accept: { fr: 'Accepter', en: 'Accept all' },
    modal_title: { fr: 'Préférences de témoins', en: 'Cookie preferences' },
    modal_intro: {
      fr: 'Choisissez les catégories de témoins que vous acceptez. Les témoins essentiels sont toujours actifs.',
      en: 'Choose which cookie categories you accept. Essential cookies are always active.'
    },
    cat_essential_title: { fr: 'Témoins essentiels', en: 'Essential cookies' },
    cat_essential_body: {
      fr: 'Nécessaires au fonctionnement du site (consentement, langue, formulaire). Toujours actifs.',
      en: 'Required for the site to function (consent, language, form). Always active.'
    },
    cat_analytics_title: { fr: 'Témoins analytiques', en: 'Analytics cookies' },
    cat_analytics_body: {
      fr: 'Mesurent la fréquentation anonymisée du site pour améliorer l\'expérience.',
      en: 'Measure anonymized site traffic to improve the experience.'
    },
    cat_marketing_title: { fr: 'Témoins marketing', en: 'Marketing cookies' },
    cat_marketing_body: {
      fr: 'Permettent de mesurer l\'efficacité des publicités (Meta Pixel, Google Ads).',
      en: 'Allow measuring ads effectiveness (Meta Pixel, Google Ads).'
    },
    save_preferences: { fr: 'Sauvegarder mes choix', en: 'Save my preferences' },
    revoke_link: { fr: 'Modifier mon consentement', en: 'Modify my consent' }
  },

  // === CALENDLY MODAL (URL pending — open question #10) ===
  calendly: {
    title: {
      fr: 'Réserver un appel',
      en: 'Book a call'
    },
    fallback_message: {
      fr: 'La prise de rendez-vous en ligne arrive bientôt. En attendant, écrivez-moi via le formulaire — je vous reviens sous 24 h ouvrables.',
      en: 'Online booking is coming soon. In the meantime, write to me via the form, I get back to you within 24 business hours.'
    },
    fallback_cta: {
      fr: 'Écrire à Océanne',
      en: 'Message Océanne'
    }
  },

  // === MOBILE STICKY BAR (4 actions Phone/SMS/Calendly/CTA) ===
  mobile: {
    call_aria: {
      fr: 'Appeler Océanne au 4 3 8 - 3 8 2 - 6 4 9 6',
      en: 'Call Océanne at 4 3 8 - 3 8 2 - 6 4 9 6'
    },
    sms_aria: {
      fr: 'Envoyer un SMS à Océanne',
      en: 'Send Océanne an SMS'
    },
    sms_body: {
      fr: 'Bonjour Océanne, j\'aimerais en savoir plus sur l\'audit gratuit du régime collectif. Pouvez-vous me rappeler ?',
      en: 'Hello Océanne, I\'d like to know more about the free group-plan audit. Could you call me back?'
    },
    calendly_aria: {
      fr: 'Réserver un appel avec Océanne',
      en: 'Book a call with Océanne'
    },
    cta_label: {
      fr: 'Audit gratuit',
      en: 'Free audit'
    },
    sms_message: {
      fr: 'Bonjour Océanne, j\'aimerais en savoir plus sur l\'audit gratuit du régime collectif. Pouvez-vous me rappeler ?',
      en: 'Hello Océanne, I\'d like to know more about the free group-plan audit. Could you call me back?'
    },
    sms_tooltip: {
      fr: 'M\'écrire par SMS',
      en: 'Message me by SMS'
    }
  },
  partners: {
    eyebrow: {
      fr: 'COMPAGNIE REPRÉSENTÉE',
      en: 'INSURER REPRESENTED'
    },
    title: {
      fr: 'Distributrice agréée d\'iA Groupe Financier',
      en: 'Licensed distributor of iA Financial Group'
    },
    description: {
      fr: 'Chef de file québécois en assurance maladie grave depuis plus d\'un siècle, iA Groupe Financier offre une gamme de produits d\'assurance maladie grave reconnue pour sa solidité et la clarté de ses garanties. Je distribue leurs produits dans le cadre de mon inscription à l\'AMF.',
      en: 'A Quebec leader in critical-illness insurance for over a century, iA Financial Group offers a critical-illness product range recognized for its solidity and clear guarantees. I distribute their products as part of my AMF registration.'
    },
    note: {
      fr: 'iA Groupe Financier et le logo iA sont des marques de commerce d\'Industrielle Alliance, Assurance et services financiers inc., utilisées sous licence.',
      en: 'iA Financial Group and the iA logo are trademarks of Industrial Alliance Insurance and Financial Services Inc., used under licence.'
    }
  },
  exit_popup: {
    eyebrow: {
      fr: 'AVANT DE PARTIR',
      en: 'BEFORE YOU GO'
    },
    title: {
      fr: 'Un audit.\nVingt minutes.',
      en: 'An audit.\nTwenty minutes.'
    },
    description: {
      fr: 'Avant de quitter cette page, donnez-vous vingt minutes pour voir clair sur votre couverture en assurance maladie grave. Sans frais, sans engagement, sans pression de souscription.',
      en: 'Before you leave this page, give yourself twenty minutes to clarify your critical-illness coverage. No fees, no commitment, no sales pressure.'
    },
    cta_button: {
      fr: 'Réserver mon audit AMG',
      en: 'Book my critical-illness audit'
    },
    cta_guide: {
      fr: 'Ou téléchargez le guide gratuit',
      en: 'Or download the free guide'
    },
    or_call_label: {
      fr: 'ou appelez directement',
      en: 'or call directly'
    },
    close_aria: {
      fr: 'Fermer',
      en: 'Close'
    }
  }
} as const;
